import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Tooltip, Drawer, Popover, Spin, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PlayCircleOutlined, HistoryOutlined, EyeOutlined, ArrowLeftOutlined, MinusCircleOutlined, PlusCircleOutlined, ExclamationCircleOutlined, BarChartOutlined, RedoOutlined, TrophyOutlined, ExportOutlined, DownOutlined } from '@ant-design/icons';
import { judgeTaskApi, judgeSetApi, judgeScenarioApi, judgeTaskExecutionApi, useCaseExecutionApi, benchmarkApi, useCaseApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { smartParseJSON } from '../../utils/jsonUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { renderAsync } from 'docx-preview';
import { TreeFieldEditor } from '../../components/TreeFieldEditor';

const { Search, TextArea } = Input;
const { Option } = Select;

function JudgeTaskList() {
  const { currentScenario, hasManagePermission, initFromUrlParams } = useWorkspace();
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [judgeSets, setJudgeSets] = useState([]);
  const [judgeScenarios, setJudgeScenarios] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    taskName: '',
    taskType: undefined,
    status: undefined,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [caseHistoryModalVisible, setCaseHistoryModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState(null);
  const [taskExecutionHistory, setTaskExecutionHistory] = useState([]);
  const [caseExecutionHistory, setCaseExecutionHistory] = useState([]);
  const [recalculatingIds, setRecalculatingIds] = useState(new Set());
  const recalculatingIdsRef = useRef(new Set());
  useEffect(() => {
    recalculatingIdsRef.current = recalculatingIds;
  }, [recalculatingIds]);
  const [caseLoading, setCaseLoading] = useState(false);
  const [casePagination, setCasePagination] = useState({
    current: 1,
    pageSize: 500,
    total: 0,
  });
  const [form] = Form.useForm();
  
  // Benchmark相关状态
  const [benchmarkModalVisible, setBenchmarkModalVisible] = useState(false);
  const [selectedExecutionRecord, setSelectedExecutionRecord] = useState(null);
  const [benchmarkForm] = Form.useForm();
  
  // 用例执行详情筛选状态
  const [caseExecutionStatusFilter, setCaseExecutionStatusFilter] = useState(undefined);
  const [caseAssertionResultFilter, setCaseAssertionResultFilter] = useState(undefined);
  const [caseAttachmentTypeFilter, setCaseAttachmentTypeFilter] = useState(undefined);
  const [caseAleTaskStatusFilter, setCaseAleTaskStatusFilter] = useState(undefined); // ALE执行状态筛选
  const [caseNameFilter, setCaseNameFilter] = useState(undefined); // 用例名称筛选

  // 用例批量重跑相关状态
  const [selectedCaseRowKeys, setSelectedCaseRowKeys] = useState([]);
  const [selectedCaseRecords, setSelectedCaseRecords] = useState([]);
  const [batchRerunLoading, setBatchRerunLoading] = useState(false);
  const [isSelectAllCases, setIsSelectAllCases] = useState(false);

  // 用例重跑本地状态跟踪（解决异步重跑后前端状态刷新延迟问题）
  const [rerunningCaseIds, setRerunningCaseIds] = useState(new Set());

  // 标注弹窗相关状态
  const [annotationModalVisible, setAnnotationModalVisible] = useState(false);
  const [annotationLoading, setAnnotationLoading] = useState(false);
  const [annotationSaving, setAnnotationSaving] = useState(false);
  const [currentAnnotationCase, setCurrentAnnotationCase] = useState(null);
  const [useCaseWithAttachment, setUseCaseWithAttachment] = useState(null);
  const [aleResultData, setAleResultData] = useState(null);
  const [expectedAnswerData, setExpectedAnswerData] = useState(null);
  const [editableAleResultData, setEditableAleResultData] = useState({});
  const [editableExpectedAnswerData, setEditableExpectedAnswerData] = useState({});
  const [editableAttachmentType, setEditableAttachmentType] = useState('');
  const [attachmentTypeOptions, setAttachmentTypeOptions] = useState([]);

  // 新增字段弹窗相关状态
  const [addFieldModalVisible, setAddFieldModalVisible] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [addingSubFieldPath, setAddingSubFieldPath] = useState(null); // 当前正在添加子字段的路径
  const [addingSubFieldParentName, setAddingSubFieldParentName] = useState(''); // 父级字段名（用于显示）
    const [rawExpectedAnswerForTree, setRawExpectedAnswerForTree] = useState(null); // 传给TreeFieldEditor的原始数据

  // 查看差异抽屉相关状态
  const [diffDrawerVisible, setDiffDrawerVisible] = useState(false);
  const [currentDiffDetails, setCurrentDiffDetails] = useState([]);
  const [currentDiffCaseName, setCurrentDiffCaseName] = useState('');

  // 断言通过率统计数据（接口返回，用于用例数超过当前页大小的情况）
  const [passRateStats, setPassRateStats] = useState(null);

  // 评测场景配置（用于机审场景的跳转打标功能）
  const [currentJudgeScenario, setCurrentJudgeScenario] = useState(null);

  // 标注详情弹窗相关状态（机审场景）
  const [annotationDetailVisible, setAnnotationDetailVisible] = useState(false);
  const [annotationDetailLoading, setAnnotationDetailLoading] = useState(false);
  const [currentAnnotationDetailRecord, setCurrentAnnotationDetailRecord] = useState(null);
  const [annotationExpectedAnswer, setAnnotationExpectedAnswer] = useState(null);
  const [annotationActualResult, setAnnotationActualResult] = useState(null);

  // 计算真实的断言统计数据
  const assertionStats = useMemo(() => {
    let passed = 0;
    let failed = 0;
    caseExecutionHistory.forEach(item => {
      try {
        const result = typeof item.assertionResult === 'string'
          ? JSON.parse(item.assertionResult)
          : item.assertionResult;
        if (result) {
          if (result.passed) {
            passed++;
          } else {
            failed++;
          }
        }
      } catch (e) {}
    });
    return { passed, failed };
  }, [caseExecutionHistory]);

  // 记录原始期望答案的格式类型：'format1' | 'format2' | 'format3' | 'format4'
  // 格式1: {"字段": ["值"]} - 非数组对象，值为数组
  // 格式2: [{"字段": "值"}] - 数组包对象，值为字符串
  // 格式3: {"字段": "值"} - 非数组对象，值为字符串
  // 格式4: [{"字段": ["值"]}] - 数组包对象，值为数组
  const [originalExpectedAnswerFormat, setOriginalExpectedAnswerFormat] = useState(null);
  
  // 保存原始期望答案JSON字符串（用于保持格式一致性）
  const [originalExpectedAnswerString, setOriginalExpectedAnswerString] = useState(null);

  // Benchmark类型（code方式，展示为中文label）
  const benchmarkTypes = [
    { code: 'BASELINE', label: '基线benchmark' },
    { code: 'ACCEPTANCE', label: '样本验收benchmark' },
    { code: 'PERIODIC', label: '线上周期评测benchmark' }
  ];

  // 根据code获取中文label
  const getBenchmarkTypeLabel = (code) => {
    const type = benchmarkTypes.find(t => t.code === code);
    return type ? type.label : code;
  };

  // 任务类型
  const taskTypes = [
    { code: 'BASELINE', label: '基线评测' },
    { code: 'ACCEPTANCE', label: '样本验收评测' },
    { code: 'PERIODIC', label: '线上评测' },
  ];
  const getTaskTypeLabel = (code) => {
    const t = taskTypes.find(item => item.code === code);
    return t ? t.label : code;
  };
  const getTaskTypeCode = (labelOrCode) => {
    // 兼容旧数据：如果传入的是中文描述，转换为code
    const byCode = taskTypes.find(item => item.code === labelOrCode);
    if (byCode) return byCode.code;
    const byLabel = taskTypes.find(item => item.label === labelOrCode);
    return byLabel ? byLabel.code : labelOrCode;
  };
  
  // 任务状态
  const taskStatuses = ['PENDING', 'RUNNING', 'SUCCESS', 'FAILED', 'CANCELLED'];

  // URL 参数初始化（支持从链接直接跳转）
  const [urlInitLoading, setUrlInitLoading] = useState(false);
  const [initialized, setInitialized] = useState(false); // 是否已完成初始化

  useEffect(() => {
    const initFromUrl = async () => {
      const domainId = urlSearchParams.get('domainId');
      const businessScenarioId = urlSearchParams.get('businessScenarioId');

      // 情况1：URL 有参数，从参数初始化
      if (domainId && businessScenarioId) {
        // 如果已有相同的场景，跳过初始化
        if (currentScenario?.id === parseInt(businessScenarioId)) {
          setInitialized(true);
          return;
        }

        setUrlInitLoading(true);
        try {
          const success = await initFromUrlParams(parseInt(domainId), parseInt(businessScenarioId));
          if (!success) {
            message.error('无法加载项目空间或业务场景信息');
            navigate('/business-domain');
            return;
          }
        } catch (error) {
          console.error('URL参数初始化失败:', error);
          message.error('参数初始化失败');
          navigate('/business-domain');
          return;
        } finally {
          setUrlInitLoading(false);
        }
      }

      setInitialized(true);
    };

    initFromUrl();
  }, [urlSearchParams]); // 只在 URL 参数变化时执行

  useEffect(() => {
    // 如果正在初始化 URL 参数，等待完成后再加载数据
    if (urlInitLoading) return;
    // 等待初始化完成
    if (!initialized) return;
    // 如果没有当前业务场景，跳转到项目空间选择页
    if (!currentScenario) {
      message.warning('请先选择项目空间和业务场景');
      navigate('/business-domain');
      return;
    }

    fetchJudgeSets();
    fetchJudgeScenarios();
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams, currentScenario, urlInitLoading, initialized]);

  // 获取用例执行历史详情（分页和筛选）
  useEffect(() => {
    if (caseHistoryModalVisible && selectedExecutionId) {
      fetchCaseExecutions();
    }
  }, [
    caseHistoryModalVisible,
    selectedExecutionId,
    casePagination.current,
    casePagination.pageSize,
    caseExecutionStatusFilter,
    caseNameFilter
  ]);

  // 轮询：当存在执行中的用例时自动刷新状态
  useEffect(() => {
    if (!caseHistoryModalVisible || !selectedExecutionId) return;

    const hasRunning = caseExecutionHistory.some(h => h.executionStatus === 'RUNNING' || rerunningCaseIds.has(h.id));
    if (!hasRunning) return;

    const timer = setInterval(() => {
      fetchCaseExecutions();
    }, 15000);

    return () => clearInterval(timer);
  }, [caseHistoryModalVisible, selectedExecutionId, caseExecutionHistory, rerunningCaseIds]);

  // 轮询：重新计算完成后提示
  useEffect(() => {
    if (recalculatingIds.size === 0) return;

    const checkCompleted = async () => {
      const idsToCheck = Array.from(recalculatingIdsRef.current);
      if (idsToCheck.length === 0) return;
      const completedIds = [];
      for (const id of idsToCheck) {
        try {
          const detail = await judgeTaskExecutionApi.getDetail(id);
          if (detail && detail.executionStatus === 'COMPLETED') {
            completedIds.push(id);
          }
        } catch (e) {
          // 忽略轮询异常，下次再试
        }
      }
      if (completedIds.length > 0) {
        setRecalculatingIds(prev => {
          const next = new Set(prev);
          completedIds.forEach(id => next.delete(id));
          return next;
        });
        message.success({
          content: `重新计算已完成（执行ID: ${completedIds.join(', ')}）`,
          key: 'recalculate-done',
          duration: 5,
        });
        // 如果当前正在查看执行历史，自动刷新
        if (historyModalVisible && selectedTaskId) {
          handleViewHistory(selectedTaskId);
        }
      }
    };

    const timer = setInterval(checkCompleted, 10000);

    // 页面重新可见时立即检查，避免后台标签页 setInterval 被浏览器节流导致通知延迟
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCompleted();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recalculatingIds, historyModalVisible, selectedTaskId]);

  const fetchCaseExecutions = async () => {
    try {
      setCaseLoading(true);
      const params = {
        taskExecutionHistoryId: selectedExecutionId,
        pageNum: casePagination.current,
        pageSize: casePagination.pageSize,
        executionStatus: caseExecutionStatusFilter || undefined,
        caseName: caseNameFilter || undefined,
      };
      const response = await useCaseExecutionApi.getList(params);
      const historyList = response.list || [];
      setCaseExecutionHistory(historyList);
      setCasePagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));

      // 清除已完成的重跑记录：只在后端状态明确为最终完成状态（SUCCESS/FAILED）时清除
      // 避免后端异步启动期间状态尚未更新为 RUNNING 时，误删本地重跑标记
      const currentPageIds = new Set(historyList.map(item => item.id));
      setRerunningCaseIds(prev => {
        const next = new Set(prev);
        historyList.forEach(item => {
          if (next.has(item.id) && (item.executionStatus === 'SUCCESS' || item.executionStatus === 'FAILED')) {
            next.delete(item.id);
          }
        });
        // 清除已不在当前页数据中的残留标记（如翻页导致）
        prev.forEach(id => {
          if (!currentPageIds.has(id)) {
            next.delete(id);
          }
        });
        return next;
      });

      // 如果有数据，获取文件类型选项（只需在第一次或 historyId 改变时获取）
      if (historyList.length > 0 && attachmentTypeOptions.length === 0) {
        try {
          // 这里可以考虑是否需要获取全量的 caseIds 来获取全量的文件类型
          // 目前先用当前页的数据
          const caseIds = [...new Set(historyList.map(item => item.caseId).filter(id => id))];
          if (caseIds.length > 0) {
            const fileTypes = await useCaseApi.getFileTypes(caseIds);
            setAttachmentTypeOptions(fileTypes || []);
          }
        } catch (err) {
          console.error('获取文件类型列表失败:', err);
        }
      }
    } catch (error) {
      console.error('获取用例执行历史失败:', error);
    } finally {
      setCaseLoading(false);
    }
  };

  const fetchJudgeSets = async () => {
    if (!currentScenario) return;

    try {
      const response = await judgeSetApi.getList({
        pageSize: 1000,
        businessScenarioId: currentScenario.id
      });
      setJudgeSets(response.list || []);
    } catch (error) {
      console.error('获取评测集列表失败:', error);
    }
  };

  const fetchJudgeScenarios = async () => {
    if (!currentScenario) return;

    try {
      const response = await judgeScenarioApi.getList({
        pageSize: 1000,
        businessScenarioId: currentScenario.id
      });
      setJudgeScenarios(response.list || []);
    } catch (error) {
      console.error('获取评测场景列表失败:', error);
    }
  };

  const fetchData = async () => {
    if (!currentScenario) return;

    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        taskName: searchParams.taskName || undefined,
        taskType: searchParams.taskType || undefined,
        businessScenarioId: currentScenario.id, // 使用当前业务场景的ID
        status: searchParams.status || undefined,
      };
      
      const response = await judgeTaskApi.getList(params);
      
      setDataSource(response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取评测任务列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, taskName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    // 如果有当前业务场景，自动设置
    if (currentScenario) {
      form.setFieldsValue({
        businessScenarioId: currentScenario.id,
      });
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalVisible(true);
    form.setFieldsValue({
      taskName: record.taskName,
      taskType: getTaskTypeCode(record.taskType),
      businessScenarioId: record.businessScenarioId,
      judgeSetId: record.judgeSetId,
      judgeScenarioId: record.judgeScenarioId,
      hasExecutionResult: record.hasExecutionResult,
    });
  };

  const handleDelete = async (id) => {
    try {
      await judgeTaskApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除评测任务失败:', error);
    }
  };

  const handleExecute = (id) => {
    Modal.confirm({
      title: '确认执行',
      content: '确定要执行这个评测任务吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await judgeTaskApi.execute(id);
          message.success('任务已开始执行');
          fetchData();
        } catch (error) {
          console.error('执行评测任务失败:', error);
        }
      }
    });
  };

  const handleViewHistory = async (taskId) => {
    try {
      setSelectedTaskId(taskId);
      const response = await judgeTaskExecutionApi.getList({ taskId, pageSize: 100 });
      setTaskExecutionHistory(response.list || []);
      setHistoryModalVisible(true);
    } catch (error) {
      console.error('获取任务执行历史失败:', error);
    }
  };

  const handleViewCaseHistory = async (record) => {
    try {
      setSelectedExecutionId(record.id);
      setSelectedHistoryRecord(record);
      setCaseExecutionHistory([]);
      setPassRateStats(null); // 重置接口返回的通过率统计
      // 重置分页和筛选条件，触发 useEffect 加载数据
      setCasePagination({
        current: 1,
        pageSize: 500,
        total: 0,
      });
      setCaseExecutionStatusFilter(undefined);
      setCaseAssertionResultFilter(undefined);
      setCaseAttachmentTypeFilter(undefined);
      setCaseAleTaskStatusFilter(undefined); // 重置ALE执行状态筛选
      setCaseNameFilter(undefined); // 重置用例名称筛选
      setAttachmentTypeOptions([]); // 清空文件类型选项，让 fetchCaseExecutions 重新获取
      // 重置批量重跑相关状态
      setSelectedCaseRowKeys([]);
      setSelectedCaseRecords([]);
      setIsSelectAllCases(false);
      
      // 加载评测场景配置（用于机审场景的跳转打标功能）
      if (record.scenarioId) {
        try {
          const scenarioDetail = await judgeScenarioApi.getDetail(record.scenarioId);
          setCurrentJudgeScenario(scenarioDetail);
        } catch (err) {
          console.error('获取评测场景详情失败:', err);
          setCurrentJudgeScenario(null);
        }
      } else {
        setCurrentJudgeScenario(null);
      }
      
      // 如果用例总数超过当前页大小，调用接口获取断言通过率
      const totalCount = record.totalCaseCount || 0;
      const pageSize = 500; // 与 casePagination.pageSize 保持一致
      if (totalCount > pageSize) {
        try {
          // 调用接口获取断言通过率
          // 接口返回格式: { totalCount, passCount, failCount, passRate }
          const passRateData = await useCaseExecutionApi.getPassRate(record.id, totalCount);
          setPassRateStats(passRateData);
        } catch (err) {
          console.error('获取断言通过率失败:', err);
        }
      }
      
      setCaseHistoryModalVisible(true);
    } catch (error) {
      console.error('获取用例执行历史失败:', error);
    }
  };

  // 备用的前端解析URL模板函数（后端接口调用失败时使用）
  const parseMarkingUrlFallback = (template, caseRecord, executionRecord) => {
    if (!template) return '';
    
    let url = template;
    
    // 替换 case.businessId
    url = url.replace(/\$\{case\.businessId\}/g, caseRecord.businessId || '');
    
    // 替换 case.businessScene
    url = url.replace(/\$\{case\.businessScene\}/g, caseRecord.businessScene || '');
    
    // 替换 case.extend.xxx
    url = url.replace(/\$\{case\.extend\.(\w+)\}/g, (match, key) => {
      try {
        const extendData = caseRecord.businessExtInfo ? JSON.parse(caseRecord.businessExtInfo) : {};
        return extendData[key] || '';
      } catch (e) {
        return '';
      }
    });
    
    // 替换 result.xxx
    url = url.replace(/\$\{result\.(\w+)\}/g, (match, key) => {
      try {
        const resultData = executionRecord.executionResult ? JSON.parse(executionRecord.executionResult) : {};
        return resultData[key] || '';
      } catch (e) {
        return '';
      }
    });
    
    return url;
  };

  // 处理跳转打标
  const handleMarkingRedirect = async (executionRecord) => {
    if (!currentJudgeScenario?.id) {
      message.error('未获取评测场景信息');
      return;
    }
    
    try {
      // 调用后端接口解析URL
      const redirectUrl = await useCaseExecutionApi.parseMarkingUrl(
        currentJudgeScenario.id,
        executionRecord.caseId,
        executionRecord.id
      );
      
      if (redirectUrl) {
        // 新开页面跳转
        window.open(redirectUrl, '_blank');
      } else {
        message.error('解析URL失败，返回结果为空');
      }
    } catch (error) {
      console.error('后端解析URL失败，尝试使用前端备用方案:', error);
      
      // 后端失败时使用前端备用方案
      if (currentJudgeScenario?.markingUrl) {
        try {
          const caseDetail = await useCaseApi.getDetail(executionRecord.caseId);
          const redirectUrl = parseMarkingUrlFallback(
            currentJudgeScenario.markingUrl,
            caseDetail,
            executionRecord
          );
          if (redirectUrl) {
            window.open(redirectUrl, '_blank');
          } else {
            message.error('解析URL失败');
          }
        } catch (fallbackError) {
          console.error('前端备用方案也失败:', fallbackError);
          message.error('跳转打标失败');
        }
      } else {
        message.error('未配置跳转URL模板');
      }
    }
  };

  // 查看标注详情（机审场景）
  const handleViewAnnotationDetail = async (executionRecord) => {
    setAnnotationDetailVisible(true);
    setAnnotationDetailLoading(true);
    setCurrentAnnotationDetailRecord(executionRecord);
    setAnnotationExpectedAnswer(null);
    setAnnotationActualResult(null);

    try {
      // 1. 获取用例信息（包含期望答案）
      const useCaseData = await useCaseApi.getDetail(executionRecord.caseId);
      
      // 保存用例的documentId（单据ID）
      if (useCaseData?.documentId) {
        setCurrentAnnotationDetailRecord(prev => ({ ...prev, documentId: useCaseData.documentId }));
      }
      
      // 2. 解析期望答案
      if (useCaseData?.expectedAnswer) {
        try {
          const expectedAnswer = typeof useCaseData.expectedAnswer === 'string'
            ? JSON.parse(useCaseData.expectedAnswer)
            : useCaseData.expectedAnswer;
          setAnnotationExpectedAnswer(expectedAnswer);
        } catch (e) {
          console.error('解析期望答案失败:', e);
          setAnnotationExpectedAnswer(useCaseData.expectedAnswer);
        }
      }

      // 3. 解析机审返回结果
      if (executionRecord.executionResultData) {
        try {
          const actualResult = typeof executionRecord.executionResultData === 'string'
            ? JSON.parse(executionRecord.executionResultData)
            : executionRecord.executionResultData;
          setAnnotationActualResult(actualResult);
        } catch (e) {
          console.error('解析机审结果失败:', e);
          setAnnotationActualResult(executionRecord.executionResultData);
        }
      }
    } catch (error) {
      console.error('获取标注详情失败:', error);
      message.error('获取标注详情失败');
    } finally {
      setAnnotationDetailLoading(false);
    }
  };

  // 关闭标注详情弹窗
  const handleCloseAnnotationDetail = () => {
    setAnnotationDetailVisible(false);
    setCurrentAnnotationDetailRecord(null);
    setAnnotationExpectedAnswer(null);
    setAnnotationActualResult(null);
  };

  // 下载报告（机审场景）
  const handleDownloadReport = async () => {
    if (!selectedExecutionId) {
      message.error('未选择执行历史');
      return;
    }
    
    const hide = message.loading('正在生成报告，请稍候...', 0);
    
    try {
      // 调用后端接口下载报告
      const response = await useCaseExecutionApi.downloadReport(selectedExecutionId);
      
      // 创建下载链接
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `用例执行报告_${selectedExecutionId}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      message.success('报告下载成功');
    } catch (error) {
      console.error('下载报告失败:', error);
      message.error('下载报告失败');
    } finally {
      hide();
    }
  };

  // 回传报告（机审场景）
  const handleUploadReport = async () => {
    if (!selectedExecutionId) {
      message.error('未选择执行历史');
      return;
    }
    
    // 创建文件输入元素
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const hide = message.loading('正在回传报告，请稍候...', 0);
      
      try {
        // 调用后端接口回传报告
        await useCaseExecutionApi.uploadReport(selectedExecutionId, file);
        message.success('报告回传成功');
        // 刷新数据
        fetchCaseExecutions();
      } catch (error) {
        console.error('回传报告失败:', error);
        message.error('回传报告失败');
      } finally {
        hide();
      }
    };
    
    input.click();
  };

  // 导出任务执行历史
  const handleExportHistory = async (record) => {
    if (!record || !record.id) {
      message.error('执行历史信息缺失');
      return;
    }

    const hide = message.loading('正在导出，请稍候...', 0);

    try {
      // 构建导出URL
      const exportUrl = `/api/judge-task/export-history?taskExecutionHistoryId=${record.id}`;

      // 使用 fetch 获取文件流
      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: {
          // 如果需要认证，添加 token
          // 'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`导出失败: ${response.status}`);
      }

      // 获取文件名（从响应头中获取，如果后端有设置）
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `task_execution_${record.id}_${Date.now()}.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
        }
      }

      // 将响应转换为 Blob
      const blob = await response.blob();

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      hide();
      message.success('导出成功');
    } catch (error) {
      hide();
      console.error('导出任务执行历史失败:', error);
      message.error('导出失败，请重试');
    }
  };

  // 查看结果统计（要素抽取和机审场景）- 浏览器新开页面
  const handleViewResultStats = (record) => {
    if (!currentScenario) {
      message.error('当前业务场景信息缺失');
      return;
    }

    if (!record.aleResult) {
      message.warning('该执行记录暂无结果统计数据');
      return;
    }

    // 根据业务场景类型和ID跳转到不同的结果统计页面
    if (currentScenario?.scenarioCategory === 'AUTO_REVIEW') {
      // 机审场景
      if (currentScenario?.id === 12) {
        // 业务场景ID为12：使用原有的特殊机审结果统计页面
        window.open(`/#/machine-audit-stats/${record.id}?taskName=${encodeURIComponent(record.taskName || '')}`, '_blank');
      } else {
        // 业务场景ID不为12：使用新的通用机审结果统计页面
        window.open(`/#/auto-review-stats/${record.id}?taskName=${encodeURIComponent(record.taskName || '')}`, '_blank');
      }
    } else {
      // 要素抽取场景跳转到通用结果统计页面
      window.open(`/#/result-stats/${record.id}?taskName=${encodeURIComponent(record.taskName || '')}`, '_blank');
    }
  };

  // 重跑单个用例
  const handleRerunCase = async (record) => {
    if (!record?.id) {
      message.error('用例执行记录信息缺失');
      return;
    }
    try {
      message.loading({ content: '正在重跑用例...', key: 'rerun_' + record.id, duration: 0 });
      await useCaseExecutionApi.rerun([record.id]);
      message.success({ content: '重跑已提交', key: 'rerun_' + record.id });
      // 本地标记为正在重跑，立即禁用按钮并显示执行中状态
      setRerunningCaseIds(prev => {
        const next = new Set(prev);
        next.add(record.id);
        return next;
      });
      // 延迟 5 秒再刷新，给后端异步启动留出足够时间
      setTimeout(() => fetchCaseExecutions(), 5000);
    } catch (error) {
      message.error({ content: '重跑失败: ' + (error.message || '未知错误'), key: 'rerun_' + record.id });
    }
  };

  // 带二次确认的单个用例重跑
  const handleRerunCaseWithConfirm = (record) => {
    Modal.confirm({
      title: '确认重跑',
      content: `确定要重跑用例「${record.caseName || record.id}」吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => handleRerunCase(record),
    });
  };

  // 批量重跑用例
  const handleBatchRerun = async () => {
    let idsToRerun = [];
    if (isSelectAllCases) {
      // 选择所有：需要调用全量查询获取所有ID
      try {
        setBatchRerunLoading(true);
        message.loading({ content: '正在获取所有用例...', key: 'batch_rerun', duration: 0 });
        const params = {
          taskExecutionHistoryId: selectedExecutionId,
          pageNum: 1,
          pageSize: 99999,
          executionStatus: caseExecutionStatusFilter || undefined,
          caseName: caseNameFilter || undefined,
        };
        const response = await useCaseExecutionApi.getList(params);
        const allItems = response.list || [];
        idsToRerun = allItems.map(item => item.id).filter(Boolean);
      } catch (error) {
        message.error({ content: '获取用例列表失败', key: 'batch_rerun' });
        setBatchRerunLoading(false);
        return;
      }
    } else {
      idsToRerun = selectedCaseRowKeys;
    }

    if (idsToRerun.length === 0) {
      message.warning('请先选择需要重跑的用例');
      setBatchRerunLoading(false);
      return;
    }

    try {
      setBatchRerunLoading(true);
      message.loading({ content: `正在重跑 ${idsToRerun.length} 个用例...`, key: 'batch_rerun', duration: 0 });
      await useCaseExecutionApi.rerun(idsToRerun);
      message.success({ content: `重跑已提交，共 ${idsToRerun.length} 个用例`, key: 'batch_rerun' });
      setSelectedCaseRowKeys([]);
      setSelectedCaseRecords([]);
      setIsSelectAllCases(false);
      // 本地标记所有批量重跑的用例为正在重跑
      setRerunningCaseIds(prev => {
        const next = new Set(prev);
        idsToRerun.forEach(id => next.add(id));
        return next;
      });
      // 延迟 5 秒再刷新，给后端异步启动留出足够时间
      setTimeout(() => fetchCaseExecutions(), 5000);
    } catch (error) {
      message.error({ content: '批量重跑失败: ' + (error.message || '未知错误'), key: 'batch_rerun' });
    } finally {
      setBatchRerunLoading(false);
    }
  };

  // 重新计算执行结果
  const handleRecalculate = async (record) => {
    Modal.confirm({
      title: '确认重新计算',
      content: `确定要对执行ID ${record.id} 进行重新计算吗？这将基于用例的执行结果重新执行断言脚本和指标计算。`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: '正在重新计算...', key: 'recalculate', duration: 0 });
          await judgeTaskExecutionApi.recalculate(record.id);
          message.success({ content: '重新计算已提交，正在后台执行，请稍后刷新查看结果', key: 'recalculate', duration: 5 });
          // 将该执行ID加入轮询监听
          setRecalculatingIds(prev => new Set(prev).add(record.id));
          // 刷新执行历史列表
          handleViewHistory(selectedTaskId);
        } catch (error) {
          message.error({ content: '重新计算失败: ' + (error.message || '未知错误'), key: 'recalculate' });
        }
      },
    });
  };

  // 标注用例（要素抽取场景）
  const handleAnnotateCase = async (caseRecord) => {
    if (!caseRecord) {
      message.error('用例信息缺失');
      return;
    }

    setCurrentAnnotationCase(caseRecord);
    setAnnotationModalVisible(true);
    setAnnotationLoading(true);

    try {
      // 1. 获取用例信息和附件信息
      const useCaseData = await useCaseApi.getWithAttachment(caseRecord.caseId);
      setUseCaseWithAttachment(useCaseData);
      setEditableAttachmentType(useCaseData?.attachmentType || '');

      // 2. 解析ALE返回的执行结果数据
      if (caseRecord.executionResultData) {
        try {
          // 将executionResultData转换为JSON字符串传入
          // 因为/api/use-case-execution/list返回时已经解密，可能是对象或字符串
          const aleResultJsonStr = typeof caseRecord.executionResultData === 'string'
            ? caseRecord.executionResultData
            : JSON.stringify(caseRecord.executionResultData);
          
          const parsedAleResult = await useCaseExecutionApi.parseAleResult(aleResultJsonStr);
          setAleResultData(parsedAleResult);
          setEditableAleResultData(parsedAleResult || {});
        } catch (parseError) {
          console.error('解析ALE结果失败:', parseError);
          setAleResultData(null);
          setEditableAleResultData({});
        }
      } else {
        setAleResultData(null);
        setEditableAleResultData({});
      }

      // 3. 获取标准答案（期望答案）
      // 兼容六种格式：
      // 格式1: {"发票号码":["25442000000426105496"],"开票日期":["2025-07-15"]} - 非数组对象，值为数组
      // 格式2: [{"发票号码":"25312000000214035521","开票日期":"2025年07月09日"}] - 数组包对象，值为字符串
      // 格式3: {"发票号码":"25312000000214035521","开票日期":"2025年07月09日"} - 非数组对象，值为字符串
      // 格式4: [{"发票号码":["25312000000214035521"],"开票日期":["2025年07月09日"]}] - 数组包对象，值为数组
      // 格式5: [{"发票号":["25041718356"],"支付明细":[{"总金额":["4200.00"],"币种":["EUR"]}]}] - 数组包对象，值为数组，且数组内可嵌套对象
      // 格式6: {"基础统计信息": {"图片数量": 6}, "互动数据统计": {"点赞数量": 18}} - 非数组对象，值为嵌套对象
      if (useCaseData?.useCaseInfo?.expectedAnswer) {
        try {
          let expectedAnswerRaw;
          
          if (typeof useCaseData.useCaseInfo.expectedAnswer === 'string') {
            // 保存原始字符串（用于保持格式一致性）
            setOriginalExpectedAnswerString(useCaseData.useCaseInfo.expectedAnswer);
            
            // 使用智能JSON解析（自动修复全角字符、控制字符等问题）
            console.log('===== 标准答案原始字符串（前200字符）=====');
            console.log(useCaseData.useCaseInfo.expectedAnswer.substring(0, 200));
            
            expectedAnswerRaw = smartParseJSON(useCaseData.useCaseInfo.expectedAnswer, true);
            
            if (expectedAnswerRaw === null) {
              throw new Error('智能JSON解析返回null');
            }
          } else {
            expectedAnswerRaw = useCaseData.useCaseInfo.expectedAnswer;
            // 如果是对象，序列化为字符串保存
            setOriginalExpectedAnswerString(JSON.stringify(expectedAnswerRaw, null, 2));
          }
          
          console.log('===== 标准答案解析后数据 =====', expectedAnswerRaw);

          // 立即保存原始数据（未标准化），供TreeFieldEditor使用
          // 必须在 detectExpectedAnswerFormat / normalizeExpectedAnswer 之前设置，
          // 避免后续处理抛异常导致 TreeFieldEditor 拿不到数据
          setRawExpectedAnswerForTree(expectedAnswerRaw);

          // 检测并记录原始格式类型
          const formatType = detectExpectedAnswerFormat(expectedAnswerRaw);
          console.log('===== 检测到的格式类型 =====', formatType);
          setOriginalExpectedAnswerFormat(formatType);

          // 使用 normalizeExpectedAnswer 统一处理三种格式
          const expectedAnswer = normalizeExpectedAnswer(expectedAnswerRaw);
          console.log('===== 标准化后的数据 =====', expectedAnswer);

          setExpectedAnswerData(expectedAnswer);
          setEditableExpectedAnswerData(expectedAnswer || {});
          console.log('===== 设置到editableExpectedAnswerData =====', expectedAnswer || {});
        } catch (e) {
          console.error('解析期望答案失败:', e);
          console.error('原始数据:', useCaseData.useCaseInfo.expectedAnswer);
          // 解析失败时，尝试直接使用原始数据
          try {
            const fallbackData = useCaseData.useCaseInfo.expectedAnswer;
            setExpectedAnswerData(fallbackData);
            setEditableExpectedAnswerData(fallbackData || {});
            // 降级：也设置给TreeFieldEditor，确保有数据展示
            if (typeof fallbackData === 'string') {
              setOriginalExpectedAnswerString(fallbackData);
              // 尝试解析字符串给TreeFieldEditor
              try {
                const parsed = JSON.parse(fallbackData);
                setRawExpectedAnswerForTree(parsed);
              } catch (_) {
                setRawExpectedAnswerForTree(null);
              }
            } else {
              setRawExpectedAnswerForTree(fallbackData);
            }
          } catch (fallbackError) {
            console.error('降级处理也失败:', fallbackError);
            setExpectedAnswerData(null);
            setEditableExpectedAnswerData({});
            setOriginalExpectedAnswerString(null);
            setRawExpectedAnswerForTree(null);
          }
          setOriginalExpectedAnswerFormat(null);
        }
      } else {
        setExpectedAnswerData(null);
        setEditableExpectedAnswerData({});
        setOriginalExpectedAnswerFormat(null);
        setOriginalExpectedAnswerString(null);
      }

      // 4. 调用字段定义接口，补全缺失字段或初始化空结构
      // caseId: 用例主键ID，taskId: selectedTaskId（当前评测任务ID）
      try {
        const schemaResult = await judgeScenarioApi.getDocumentSchemaWithAlias(
          caseRecord.caseId,
          selectedTaskId
        );
        if (schemaResult && typeof schemaResult === 'object' && Object.keys(schemaResult).length > 0) {
          // 兼容 Result 包装对象（取 content）和直接返回的 Map
          const schemaFields = schemaResult.content || schemaResult;
          if (schemaFields && typeof schemaFields === 'object') {
            // 获取当前 rawExpectedAnswerForTree（可能是数组或对象）
            // 先拿到扁平对象层做 key 比较
            let currentRaw = null;
            // 读取当前已设置的 rawExpectedAnswerForTree 快照
            // 由于 useState 异步，直接从 useCaseData 重新解析
            const rawAnswer = useCaseData?.useCaseInfo?.expectedAnswer;
            if (rawAnswer) {
              try {
                const parsed = typeof rawAnswer === 'string' ? smartParseJSON(rawAnswer, false) : rawAnswer;
                currentRaw = parsed;
              } catch (_) {
                currentRaw = null;
              }
            }

            // 判断是否是数组格式（取第一个元素做比较）
            const isArrayFormat = Array.isArray(currentRaw);
            const flatAnswer = isArrayFormat ? (currentRaw[0] || {}) : (currentRaw || {});

            // 判断字段定义中是否有 key 不在期望答案中（即字段不全）
            const schemaKeys = Object.keys(schemaFields);
            const answerKeys = Object.keys(flatAnswer);
            const hasMissingKeys = schemaKeys.some(k => !answerKeys.includes(k));

            if (!rawAnswer || hasMissingKeys) {
              // 没有期望答案，或期望答案字段不全：以字段定义为骨架，回填已有值
              const createEmptyStructure = (obj) => {
                const result = {};
                Object.keys(obj).forEach(key => {
                  const value = obj[key];
                  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                    result[key] = createEmptyStructure(value);
                  } else {
                    result[key] = '';
                  }
                });
                return result;
              };
              const merged = {};
              schemaKeys.forEach(k => {
                if (Object.prototype.hasOwnProperty.call(flatAnswer, k)) {
                  merged[k] = flatAnswer[k];
                } else {
                  const schemaValue = schemaFields[k];
                  if (schemaValue !== null && typeof schemaValue === 'object' && !Array.isArray(schemaValue)) {
                    merged[k] = createEmptyStructure(schemaValue);
                  } else {
                    merged[k] = '';
                  }
                }
              });
              // 期望答案中多出的 key 也保留（允许多填）
              answerKeys.forEach(k => {
                if (!Object.prototype.hasOwnProperty.call(merged, k)) {
                  merged[k] = flatAnswer[k];
                }
              });
              // 还原为原始格式
              const mergedForTree = isArrayFormat ? [merged] : merged;
              console.log('===== 字段定义合并后传给TreeFieldEditor =====', mergedForTree);
              setRawExpectedAnswerForTree(mergedForTree);
            }
          }
        }
      } catch (schemaError) {
        // 字段定义接口失败，降级使用原有期望答案，不影响主流程
        console.warn('获取字段定义失败，降级使用原有期望答案:', schemaError);
        // 兜底：如果没有任何标准答案数据，给 TreeFieldEditor 一个空对象，确保可以正常新增字段
        const rawAnswer = useCaseData?.useCaseInfo?.expectedAnswer;
        if (!rawAnswer) {
          setRawExpectedAnswerForTree({});
        }
      }
    } catch (error) {
      console.error('获取标注数据失败:', error);
      message.error('获取标注数据失败');
    } finally {
      setAnnotationLoading(false);
    }
  };

  // 关闭标注弹窗
  const handleCloseAnnotationModal = () => {
    setAnnotationModalVisible(false);
    setCurrentAnnotationCase(null);
    setUseCaseWithAttachment(null);
    setAleResultData(null);
    setExpectedAnswerData(null);
    setEditableAleResultData({});
    setEditableExpectedAnswerData({});
    setEditableAttachmentType('');
    setOriginalExpectedAnswerString(null);
    setRawExpectedAnswerForTree(null);
  };

  // 保存标注数据（更新用例执行历史的标注结果）
  const handleSaveAnnotation = async () => {
    if (!currentAnnotationCase?.id) {
      message.error('用例执行历史信息缺失');
      return;
    }

    setAnnotationSaving(true);
    try {
      // 保存标注时，保存的是标准答案数据（editableExpectedAnswerData）
      // 因为用户是在标准答案区域进行标注和新增字段的
      let dataToSave = { ...editableExpectedAnswerData };
      
      // 检查是否有数据需要保存
      if ((!dataToSave || Object.keys(dataToSave).length === 0) && !editableAttachmentType) {
        message.warning('没有可保存的标注数据');
        setAnnotationSaving(false);
        return;
      }
      
      // 将文件类型添加到保存数据中（要素抽取场景需要）
      if (editableAttachmentType) {
        dataToSave.attachmentType = editableAttachmentType;
      }
      
      // 如果有原始格式，尝试保持格式一致性
      let markResult;
      if (originalExpectedAnswerString) {
        try {
          // 根据原始格式类型，将编辑后的数据还原为原始格式
          const denormalizedData = denormalizeExpectedAnswer(dataToSave, originalExpectedAnswerFormat);
          
          // 使用与原始字符串相同的格式（包括缩进等）
          markResult = JSON.stringify(denormalizedData, null, 2);
          console.log('保存标注-使用原始格式还原:', markResult.substring(0, 200));
        } catch (e) {
          console.warn('格式还原失败，使用默认格式:', e);
          markResult = JSON.stringify(dataToSave, null, 2);
        }
      } else {
        markResult = JSON.stringify(dataToSave, null, 2);
      }

      await useCaseExecutionApi.updateMarkResult({
        id: currentAnnotationCase.id,
        markResult: markResult,
      });
      message.success('保存成功');
      // 关闭弹窗
      handleCloseAnnotationModal();
    } catch (error) {
      console.error('保存标注数据失败:', error);
      message.error('保存失败');
    } finally {
      setAnnotationSaving(false);
    }
  };

  // 同步标准答案（调用更新用例期望答案接口，仅更新expected_answer字段）
  const handleSyncExpectedAnswer = async () => {
    if (!currentAnnotationCase?.caseId) {
      message.error('用例信息缺失');
      return;
    }

    setAnnotationSaving(true);
    try {
      // 根据原始格式类型还原数据格式
      const denormalizedData = denormalizeExpectedAnswer(editableExpectedAnswerData, originalExpectedAnswerFormat);
      // 期望答案字段由标准答案数据组成的JSON格式
      const expectedAnswer = JSON.stringify(denormalizedData, null, 2);
      console.log('expectedAnswer--------', expectedAnswer);
      
      // 构建请求数据，包含文件类型（要素抽取场景需要）
      const requestData = {
        expectedAnswer: expectedAnswer,
      };
      if (editableAttachmentType) {
        requestData.attachmentType = editableAttachmentType;
      }
      
      // 调用新接口，更新期望答案和文件类型
      await useCaseApi.updateExpectedAnswer(currentAnnotationCase.caseId, requestData);
      message.success('同步标准答案成功');
      // 关闭弹窗
      handleCloseAnnotationModal();
    } catch (error) {
      console.error('同步标准答案失败:', error);
      message.error('同步标准答案失败');
    } finally {
      setAnnotationSaving(false);
    }
  };

  /**
   * 检测期望答案的原始格式类型
   * 格式1: {"\u5b57\u6bb5": ["\u503c"]} - 非数组对象，值为数组
   * 格式2: [{"\u5b57\u6bb5": "\u503c"}] - 数组包对象，值为字符串
   * 格式3: {"\u5b57\u6bb5": "\u503c"} - 非数组对象，值为字符串
   * 格式4: [{"\u5b57\u6bb5": ["\u503c"]}] - 数组包对象，值为数组
   * 格式5: [{"\u5b57\u6bb5": [{"\u5d4c\u5957\u5b57\u6bb5": ["\u503c"]}]}] - 数组包对象，值为数组，且数组内可嵌套对象
   * 格式6: {"字段1": ["值"], "字段2": {...}} - 非数组对象，值类型混合（数组/对象/字符串）
   * 嵌套格式: {"jiaoguan_contract": {"税率": "", ...}} - 文件类型 -> 字段对象
   */
  const detectExpectedAnswerFormat = (rawData) => {
    if (!rawData) return null;
    console.log('detectExpectedAnswerFormat-------', rawData);
    // 判断是否为数组格式
    if (Array.isArray(rawData)) {
      // 数组包对象，需要进一步判断内部对象的值是数组还是字符串
      if (rawData.length > 0 && typeof rawData[0] === 'object') {
        const innerObject = rawData[0];
        const firstKey = Object.keys(innerObject)[0];
        if (firstKey) {
          const firstValue = innerObject[firstKey];
          // 判断是格式4还是格式5
          if (Array.isArray(firstValue)) {
            // 需要检查数组内是否有嵌套对象
            let hasNestedObject = false;
            for (const key of Object.keys(innerObject)) {
              const value = innerObject[key];
              if (Array.isArray(value) && value.length > 0) {
                // 检查数组的第一个元素是否是对象
                if (typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0])) {
                  hasNestedObject = true;
                  break;
                }
              }
            }
            // 格式5: [{"\u5b57\u6bb5": [{"\u5d4c\u5957\u5b57\u6bb5": ["\u503c"]}]}] - 数组包对象，值为数组，且数组内可嵌套对象
            if (hasNestedObject) {
              return 'format5';
            }
            // 格式4: [{"\u5b57\u6bb5": ["\u503c"]}] - 数组包对象，值为数组
            return 'format4';
          }
        }
      }
      // 格式2: [{"\u5b57\u6bb5": "\u503c"}] - 数组包对象，值为字符串
      return 'format2';
    }
  
    // 非数组格式
    if (typeof rawData === 'object') {
      const keys = Object.keys(rawData);
      
      // 检查是否为嵌套格式：{文件类型: {字段: 值}}
      // 判断标准：只有一个key，且该key的值是对象（非数组）
      if (keys.length === 1) {
        const firstKey = keys[0];
        const firstValue = rawData[firstKey];
        if (firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
          console.log('检测到嵌套格式:', firstKey);
          return 'format_nested';
        }
      }
      
      // 格式6: 存在值为纯对象（非数组）的字段 → 混合值类型，优先判断
      const hasMixedObjectValue = Object.values(rawData).some(
        (v) => v !== null && typeof v === 'object' && !Array.isArray(v)
      );
      if (hasMixedObjectValue) {
        return 'format6';
      }

      const firstKey = Object.keys(rawData)[0];
      if (firstKey) {
        const firstValue = rawData[firstKey];
        // 格式1: {"\u5b57\u6bb5": ["\u503c"]} - 值为数组
        if (Array.isArray(firstValue)) {
          return 'format1';
        }
      }
      // 格式3: {"\u5b57\u6bb5": "\u503c"} - 值为字符串
      return 'format3';
    }
  
    return null;
  };

  /**
   * 将标准化后的数据还原为原始格式
   * @param normalizedData 标准化后的数据 {"\u5b57\u6bb5": ["\u503c"]}
   * @param formatType 原始格式类型 'format1' | 'format2' | 'format3' | 'format4' | 'format5' | 'format6' | 'format_nested'
   */
  const denormalizeExpectedAnswer = (normalizedData, formatType) => {
    console.log('denormalizeExpectedAnswer', normalizedData, formatType);
    if (!normalizedData || !formatType) {
      return normalizedData;
    }
    
    // 辅助函数：递归转换 _value 格式为普通值
    const convertValueObject = (data) => {
      if (!data || typeof data !== 'object') {
        return data;
      }
      
      if (Array.isArray(data)) {
        return data.map(item => convertValueObject(item));
      }
      
      // 检查是否是 _value 格式
      if (Object.keys(data).length === 1 && '_value' in data) {
        return data._value;
      }
      
      // 递归处理对象的每个字段
      const result = {};
      for (const key of Object.keys(data)) {
        result[key] = convertValueObject(data[key]);
      }
      return result;
    };
    
    // 先转换 _value 格式
    const convertedData = convertValueObject(normalizedData);
  
    switch (formatType) {
      case 'format1':
        // 格式1: {"\u5b57\u6bb5": ["\u503c"]} - 非数组对象，值为数组，直接返回
        return convertedData;
  
      case 'format2':
        // 格式2: [{"\u5b57\u6bb5": "\u503c"}] - 数组包对象，值为字符串
        const format2Object = {};
        for (const key of Object.keys(convertedData)) {
          const value = convertedData[key];
          // 取数组的第一个元素作为值
          format2Object[key] = Array.isArray(value) ? (value[0] ?? '') : value;
        }
        return [format2Object];
  
      case 'format3':
        // 格式3: {"\u5b57\u6bb5": "\u503c"} - 非数组对象，值为字符串
        const format3Object = {};
        for (const key of Object.keys(convertedData)) {
          const value = convertedData[key];
          // 取数组的第一个元素作为值
          format3Object[key] = Array.isArray(value) ? (value[0] ?? '') : value;
        }
        return format3Object;
  
      case 'format4':
        // 格式4: [{"\u5b57\u6bb5": ["\u503c"]}] - 数组包对象，值为数组
        return [convertedData];
  
      case 'format5':
        // 格式5: [{"\u5b57\u6bb5": [{"\u5d4c\u5957\u5b57\u6bb5": ["\u503c"]}]}] - 数组包对象，值为数组，且数组内可嵌套对象
        // 直接返回数组格式，保持嵌套结构
        return [convertedData];

      case 'format6':
        // 格式6: 非数组对象，值类型混合（数组/对象/字符串），直接返回，保持原始结构
        return convertedData;
      
      case 'format_nested':
        // 嵌套格式: {"jiaoguan_contract": {"税率": "", ...}} - 文件类型 -> 字段对象
        // 需要将标准化后的数据重新包装为嵌套格式
        const fileType = editableAttachmentType || 'default';
        console.log('还原嵌套格式，fileType:', fileType);
        return {
          [fileType]: convertedData
        };
  
      default:
        return convertedData;
    }
  };

  /**
   * 标准化期望答案格式
   * 兼容六种格式：
   * 格式1: {"\u53d1\u7968\u53f7\u7801": ["25312000000214035521"], ...} - 值为单元素数组的对象
   * 格式2: [{"\u53d1\u7968\u53f7\u7801": "25312000000214035521", ...}] - 数组包对象
   * 格式3: {"\u53d1\u7968\u53f7\u7801": "25312000000214035521", ...} - 普通扁平JSON对象
   * 格式4: [{"\u53d1\u7968\u53f7\u7801": ["25312000000214035521"], ...}] - 数组包对象，值为数组
   * 格式5: [{"\u53d1\u7968\u53f7": ["25041718356"], "\u652f\u4ed8\u660e\u7ec6": [{"\u603b\u91d1\u989d": ["4200.00"]}]}] - 值为数组，且数组内可嵌套对象
   * 格式6: {"字段1": ["值"], "字段2": {...}} - 非数组对象，值类型混合（数组/对象/字符串）
   * 嵌套格式: {"jiaoguan_contract": {"税率": "", ...}} - 文件类型 -> 字段对象
   * 
   * 统一转换为：{"\u53d1\u7968\u53f7\u7801": ["25312000000214035521"], ...} 格式（格式5/6保持嵌套结构）
   */
  const normalizeExpectedAnswer = (rawData) => {
    if (!rawData) return null;
  
    let dataObject = rawData;
  
    // 如果是数组，取第一个元素
    if (Array.isArray(rawData)) {
      if (rawData.length === 0) return null;
      dataObject = rawData[0];
    }
  
    // 如果不是对象，返回null
    if (!dataObject || typeof dataObject !== 'object') {
      return null;
    }
    
    // 检查是否为嵌套格式：{文件类型: {字段: 值}}
    // 判断标准：只有一个key，且该key的值是对象（非数组）
    const keys = Object.keys(dataObject);
    if (keys.length === 1) {
      const firstKey = keys[0];
      const firstValue = dataObject[firstKey];
      if (firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
        // 这是嵌套格式，需要展开
        console.log('检测到嵌套格式，展开数据:', firstKey, firstValue);
        dataObject = firstValue;  // 使用内层对象作为数据
      }
    }
  
    // 标准化每个字段的值
    const normalizedData = {};
    for (const key of Object.keys(dataObject)) {
      const value = dataObject[key];
      if (Array.isArray(value)) {
        // 已经是数组格式，直接使用（保持嵌套对象结构）
        normalizedData[key] = value;
      } else if (value !== null && value !== undefined && typeof value === 'object') {
        // 纯对象（非数组），直接保留原始结构，不做转换
        normalizedData[key] = value;
      } else if (value !== null && value !== undefined) {
        // 基本类型（字符串/数字等），转换为数组
        normalizedData[key] = [String(value)];
      } else {
        // null 或 undefined，设为空数组
        normalizedData[key] = [''];
      }
    }
  
    return normalizedData;
  };

  /**
   * 判断是否显示"同步标准答案"按钮
   * 显示条件：
   * 1. 用例的归属业务场景 = 当前用户进入的业务场景
   * 2. 用例的可操作业务场景包含当前用户进入的业务场景
   */
  const canSyncExpectedAnswer = () => {
    if (!useCaseWithAttachment?.useCaseInfo || !currentScenario) {
      return false;
    }

    const useCaseInfo = useCaseWithAttachment.useCaseInfo;
    const currentScenarioId = currentScenario.id;

    // 条件1: 用例的归属业务场景 = 当前用户进入的业务场景
    const belongsToCurrentScenario = useCaseInfo.scenarioId === currentScenarioId;
        console.log('按钮',  useCaseInfo.scenarioId, currentScenarioId);
    // 条件2: 用例的可操作业务场景包含当前用户进入的业务场景
    let actionableScenarioIds = [];
    if (useCaseInfo.actionableScenarioIds) {
      if (typeof useCaseInfo.actionableScenarioIds === 'string') {
        try {
          actionableScenarioIds = JSON.parse(useCaseInfo.actionableScenarioIds);
        } catch (e) {
          actionableScenarioIds = useCaseInfo.actionableScenarioIds.split(',').map(id => parseInt(id.trim(), 10));
        }
      } else if (Array.isArray(useCaseInfo.actionableScenarioIds)) {
        actionableScenarioIds = useCaseInfo.actionableScenarioIds;
      }
    }
    const isActionable = actionableScenarioIds.includes(currentScenarioId);

    return belongsToCurrentScenario || isActionable;
  };

  /**
   * 处理接口返回数据字段变更（支持嵌套路径）
   * @param parentPath 父级路径数组，如 ["海外形式发票", "字段3"]
   * @param fieldKey 字段key
   * @param value 新值
   */
  const handleAleFieldChange = (parentPath, fieldKey, value) => {
    setEditableAleResultData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));

      // 根据路径找到目标对象
      let target = newData;
      for (const key of parentPath) {
        if (target[key] === undefined) {
          target[key] = {};
        }
        target = target[key];
      }

      // 设置字段值
      target[fieldKey] = value;

      return newData;
    });
  };

  /**
   * 处理标准答案字段变更（支持嵌套路径和数组索引）
   * @param parentPath 父级路径数组，可包含数组索引
   * @param fieldKey 字段key
   * @param value 新值
   */
  const handleExpectedAnswerFieldChange = (parentPath, fieldKey, value) => {
    setEditableExpectedAnswerData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));

      // 根据路径找到目标对象，支持数组索引
      let target = newData;
      for (const key of parentPath) {
        if (target[key] === undefined) {
          // 如果key是数字，说明是数组索引
          if (typeof key === 'number') {
            target[key] = {};
          } else {
            target[key] = {};
          }
        }
        target = target[key];
      }

      // 设置字段值
      target[fieldKey] = value;

      return newData;
    });
  };

  /**
   * 删除标准答案字段（文件类型除外）
   * @param parentPath 父级路径数组
   * @param fieldKey 要删除的字段名
   */
  const handleExpectedAnswerFieldDelete = (parentPath, fieldKey) => {
    setEditableExpectedAnswerData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));

      // 根据路径找到目标对象
      let target = newData;
      for (const key of parentPath) {
        if (target[key] === undefined) {
          return prevData; // 路径不存在，不做任何操作
        }
        target = target[key];
      }

      // 删除字段
      if (target && typeof target === 'object') {
        delete target[fieldKey];
      }

      return newData;
    });
  };

  /**
   * 新增标准答案字段（支持多级嵌套）
   * @param fieldKey 新字段名
   * @param fieldValue 新字段值
   * @param targetPath 目标路径数组，指定在哪个对象下新增（可选，默认根层）
   */
  const handleExpectedAnswerFieldAdd = (fieldKey, fieldValue, targetPath = []) => {
    if (!fieldKey || !fieldKey.trim()) {
      message.warning('请输入字段名');
      return false;
    }

    setEditableExpectedAnswerData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData || {}));
      
      // 如果有 targetPath，在指定路径下新增
      if (targetPath.length > 0) {
        let targetObj = newData;
        for (const pathKey of targetPath) {
          if (targetObj && targetObj[pathKey] !== undefined) {
            targetObj = targetObj[pathKey];
          } else {
            message.error('目标路径不存在');
            return prevData;
          }
        }
        
        // 在目标对象下新增字段，必须保持嵌套对象结构
        if (targetObj && typeof targetObj === 'object' && !Array.isArray(targetObj)) {
          // 关键：新增的字段初始化为空对象，确保可以继续添加子字段
          // 如果提供了 fieldValue，则将其作为初始值
          // 注意：即使没有初始值，也要确保结构一致性，使用空字符串作为 _value
          if (fieldValue) {
            targetObj[fieldKey.trim()] = { _value: fieldValue };
          } else {
            // 修复：新增的空字段也要使用 { _value: '' } 结构，避免被误判为可展开的嵌套对象
            targetObj[fieldKey.trim()] = { _value: '' };
          }
        } else {
          message.warning('无法在数组中添加字段');
          return prevData;
        }
      } else {
        // 没有 targetPath，在根层新增（兼容原有逻辑）
        // 判断是扁平格式还是嵌套格式
        const isFlat = isFlatFormat(newData);
        
        // 检查是否为空对象
        const isEmpty = Object.keys(newData).length === 0;
        
        if (isFlat || isEmpty) {
          // 扁平格式或空对象：直接在根层添加字段
          // 修复：使用对象格式 { _value: fieldValue } 而不是数组格式，以支持继续添加子字段
          newData[fieldKey.trim()] = { _value: fieldValue || '' };
        } else {
          // 嵌套格式：在文件类型key下添加字段
          const fileTypeKey = Object.keys(newData)[0];
          if (fileTypeKey && newData[fileTypeKey]) {
            // 修复：使用对象格式 { _value: fieldValue } 而不是字符串格式，以支持继续添加子字段
            newData[fileTypeKey][fieldKey.trim()] = { _value: fieldValue || '' };
          } else {
            // 兜底：直接在根层添加字段
            newData[fieldKey.trim()] = { _value: fieldValue || '' };
          }
        }
      }

      return newData;
    });
    return true;
  };

  /**
   * 处理新增子字段
   * @param parentPath 父级路径数组
   * @param parentKey 父级字段名
   */
  const handleAddSubField = (parentPath, parentKey) => {
    setAddingSubFieldPath(parentPath);
    setAddingSubFieldParentName(parentKey);
    setNewFieldName('');
    setNewFieldValue('');
    setAddFieldModalVisible(true);
  };

  /**
   * 确认新增字段
   */
  const handleConfirmAddField = () => {
    let success;
    if (addingSubFieldPath) {
      // 在指定路径下新增子字段
      success = handleExpectedAnswerFieldAdd(newFieldName, newFieldValue, addingSubFieldPath);
    } else {
      // 在根层新增字段（原有逻辑）
      success = handleExpectedAnswerFieldAdd(newFieldName, newFieldValue);
    }
    
    if (success) {
      setAddFieldModalVisible(false);
      setNewFieldName('');
      setNewFieldValue('');
      setAddingSubFieldPath(null);
      setAddingSubFieldParentName('');
    }
  };

  /**
   * 取消新增字段
   */
  const handleCancelAddField = () => {
    setAddFieldModalVisible(false);
    setNewFieldName('');
    setNewFieldValue('');
    setAddingSubFieldPath(null);
    setAddingSubFieldParentName('');
  };

  /**
   * 获取统一排序后的字段列表
   * 规则：
   * 1. 第一个字段固定为"文件类型"
   * 2. 其它字段按照返回的顺序平铺
   * 3. 接口返回数据和标准答案展示的字段顺序需要一致
   * 4. 如果有一方多了或者少了，就按照自然顺序展示，优先展示相同的字段
   */
  const getUnifiedFieldOrder = (aleData, expectedData) => {
    const aleFields = aleData ? Object.keys(aleData) : [];
    const expectedFields = expectedData ? Object.keys(expectedData) : [];

    // 获取所有字段（去重）
    const allFieldsSet = new Set([...aleFields, ...expectedFields]);
    const allFields = Array.from(allFieldsSet);

    // 找出共同字段和独有字段
    const commonFields = allFields.filter(field => aleFields.includes(field) && expectedFields.includes(field));
    const aleOnlyFields = aleFields.filter(field => !expectedFields.includes(field));
    const expectedOnlyFields = expectedFields.filter(field => !aleFields.includes(field));

    // 按照规则排序：优先展示相同的字段，然后是各自独有的字段
    return [...commonFields, ...aleOnlyFields, ...expectedOnlyFields];
  };

  /**
   * 渲染单个字段（支持嵌套展示）
   * @param fieldKey 字段名
   * @param fieldValue 字段值
   * @param parentPath 父级路径数组，用于构建完整的字段路径
   * @param isEditable 是否可编辑
   * @param onFieldChange 字段变更回调
   * @param onFieldDelete 字段删除回调
   * @param onAddSubField 新增子字段回调
   * @param level 嵌套层级
   */
  const renderField = (fieldKey, fieldValue, parentPath, isEditable, onFieldChange, onFieldDelete, onAddSubField, level = 0) => {
    // 检查是否是使用 _value 存储实际值的嵌套对象（新增的二级、三级字段）
    const isNestedValueObject = fieldValue !== null && 
                                 typeof fieldValue === 'object' && 
                                 !Array.isArray(fieldValue) && 
                                 Object.keys(fieldValue).length === 1 && 
                                 '_value' in fieldValue;
    
    const isNestedObject = fieldValue !== null && typeof fieldValue === 'object' && !Array.isArray(fieldValue) && !isNestedValueObject;
    const indent = level * 16;
    
    // 处理使用 _value 存储的嵌套对象（新增的二级、三级字段）
    if (isNestedValueObject) {
      const actualValue = fieldValue._value;
      return (
        <div key={fieldKey} className="border-b pb-2">
          <div className="flex items-center" style={{ paddingLeft: indent }}>
            <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
            {isEditable && onFieldChange ? (
              <Input
                value={actualValue || ''}
                onChange={(e) => {
                  // 更新 _value 字段
                  onFieldChange(parentPath, fieldKey, { _value: e.target.value });
                }}
                className="flex-1"
                placeholder="请输入字段值"
              />
            ) : (
              <span className="text-gray-900 flex-1">{actualValue || '-'}</span>
            )}
            {isEditable && (
              <div className="flex items-center gap-2 ml-2">
                {onFieldDelete && (
                  <Tooltip title="删除字段">
                    <MinusCircleOutlined
                      className="text-red-500 cursor-pointer hover:text-red-700"
                      onClick={() => onFieldDelete(parentPath, fieldKey)}
                    />
                  </Tooltip>
                )}
                {onAddSubField && (
                  <Tooltip title="在此字段下新增子字段">
                    <PlusCircleOutlined
                      className="text-blue-500 cursor-pointer hover:text-blue-700"
                      onClick={() => onAddSubField([...parentPath, fieldKey], fieldKey)}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
      
    if (isNestedObject) {
      const nestedFields = Object.keys(fieldValue);
      const hasValue = '_value' in fieldValue;
      const actualValue = hasValue ? fieldValue._value : null;
      
      return (
        <div key={fieldKey} className="border-b pb-2">
          <div className="flex items-center" style={{ paddingLeft: indent }}>
            <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
            {isEditable && onFieldChange && hasValue ? (
              <Input
                value={actualValue || ''}
                onChange={(e) => {
                  // 更新 _value 字段，保持其他子字段不变
                  const updatedObj = { ...fieldValue, _value: e.target.value };
                  onFieldChange(parentPath, fieldKey, updatedObj);
                }}
                className="flex-1"
                placeholder="请输入字段值"
              />
            ) : hasValue ? (
              <span className="text-gray-900 flex-1">{actualValue || '-'}</span>
            ) : null}
            {isEditable && (
              <div className="flex items-center gap-2 ml-2">
                {onFieldDelete && (
                  <Tooltip title="删除字段">
                    <MinusCircleOutlined
                      className="text-red-500 cursor-pointer hover:text-red-700"
                      onClick={() => onFieldDelete(parentPath, fieldKey)}
                    />
                  </Tooltip>
                )}
                {onAddSubField && (
                  <Tooltip title="在此字段下新增子字段">
                    <PlusCircleOutlined
                      className="text-blue-500 cursor-pointer hover:text-blue-700"
                      onClick={() => onAddSubField([...parentPath, fieldKey], fieldKey)}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          {/* 渲染子字段（排除 _value） */}
          {nestedFields.length > (hasValue ? 1 : 0) && (
            <div className="mt-1">
              {nestedFields
                .filter(key => key !== '_value')
                .map((nestedKey) =>
                  renderField(
                    nestedKey,
                    fieldValue[nestedKey],
                    [...parentPath, fieldKey],
                    isEditable,
                    onFieldChange,
                    onFieldDelete,
                    onAddSubField,
                    level + 1
                  )
                )}
            </div>
          )}
        </div>
      );
    }
    
    if (fieldValue === undefined) {
      return (
        <div key={fieldKey} className="flex items-center border-b pb-2" style={{ paddingLeft: indent }}>
          <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
          <span className="text-gray-400 flex-1">-</span>
          {isEditable && onFieldDelete && (
            <Tooltip title="删除字段">
              <MinusCircleOutlined
                className="ml-2 text-red-500 cursor-pointer hover:text-red-700"
                onClick={() => onFieldDelete(parentPath, fieldKey)}
              />
            </Tooltip>
          )}
        </div>
      );
    }
    
    // 处理数组格式的值
    if (Array.isArray(fieldValue)) {
      // 检查数组的第一个元素是否是对象（格式5：嵌套对象数组）
      if (fieldValue.length > 0 && typeof fieldValue[0] === 'object' && fieldValue[0] !== null && !Array.isArray(fieldValue[0])) {
        // 嵌套对象数组，逐个展开渲染
        return (
          <div key={fieldKey} className="border-b pb-2">
            <div className="flex items-center" style={{ paddingLeft: indent }}>
              <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
              {isEditable && onFieldDelete && (
                <Tooltip title="删除字段">
                  <MinusCircleOutlined
                    className="ml-2 text-red-500 cursor-pointer hover:text-red-700"
                    onClick={() => onFieldDelete(parentPath, fieldKey)}
                  />
                </Tooltip>
              )}
            </div>
            <div className="mt-1">
              {fieldValue.map((nestedObj, index) => {
                if (typeof nestedObj === 'object' && nestedObj !== null) {
                  return (
                    <div key={`${fieldKey}-${index}`} className="ml-4 mb-2 border-l-2 border-blue-200 pl-2">
                      {Object.keys(nestedObj).map((nestedKey) =>
                        renderField(
                          nestedKey,
                          nestedObj[nestedKey],
                          [...parentPath, fieldKey, index],
                          isEditable,
                          onFieldChange,
                          onFieldDelete,
                          onAddSubField,
                          level + 1
                        )
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        );
      }
          
      // 普通数组：["\u50241", "\u50242", ...]  逗号分隔展示
      // 编辑模式：展示所有元素拼接形式，保存时按逗号分割还原为数组
      const displayValue = fieldValue.join(', ');
      // 可编辑时也展示全部元素，避免多元素数组丢失数据
      const editValue = fieldValue.join(', ');
                
      return (
        <div key={fieldKey} className="flex items-center border-b pb-2" style={{ paddingLeft: indent }}>
          <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
          {isEditable && onFieldChange ? (
            <Input
              value={editValue}
              onChange={(e) => {
                // 按逗号分割还原数组，过滤空字符串
                const newArr = e.target.value.split(',').map((v) => v.trim()).filter((v) => v !== '');
                onFieldChange(parentPath, fieldKey, newArr.length > 0 ? newArr : ['']);
              }}
              className="flex-1"
            />
          ) : (
            <span className="text-gray-900 flex-1">{displayValue || '-'}</span>
          )}
          {isEditable && onFieldDelete && (
            <Tooltip title="删除字段">
              <MinusCircleOutlined
                className="ml-2 text-red-500 cursor-pointer hover:text-red-700"
                onClick={() => onFieldDelete(parentPath, fieldKey)}
              />
            </Tooltip>
          )}
        </div>
      );
    }
        
    // 非数组、非对象的值（字符串、数字等）
    return (
      <div key={fieldKey} className="flex items-center border-b pb-2" style={{ paddingLeft: indent }}>
        <span className="font-medium text-gray-700 w-32 flex-shrink-0">{fieldKey}：</span>
        {isEditable && onFieldChange ? (
          <Input
            value={fieldValue}
            onChange={(e) => onFieldChange(parentPath, fieldKey, e.target.value)}
            className="flex-1"
          />
        ) : (
          <span className="text-gray-900 flex-1">{fieldValue || '-'}</span>
        )}
        {isEditable && onFieldDelete && (
          <Tooltip title="删除字段">
            <MinusCircleOutlined
              className="ml-2 text-red-500 cursor-pointer hover:text-red-700"
              onClick={() => onFieldDelete(parentPath, fieldKey)}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  /**
   * 判断数据是否为扁平格式
   * 扁平格式：{"字段名": ["值"], "字段名2": ["值2"]} - 值为数组
   * 嵌套格式：{"文件类型key": {"字段名": "值"}} - 第一个值是对象
   * 混合格式：{"分组1": {"字段": "值"}, "分组2": {"字段": "值"}} - 多个分组对象
   */
  const isFlatFormat = (data) => {
    if (!data || typeof data !== 'object') return false;
    const keys = Object.keys(data);
    if (keys.length === 0) return false;
    
    console.log('===== isFlatFormat 检查 =====');
    console.log('data:', data);
    console.log('keys:', keys);
    
    // 检查所有值，如果有任何一个是数组，就是扁平格式
    for (const key of keys) {
      const value = data[key];
      console.log(`检查 ${key}:`, value, 'isArray:', Array.isArray(value));
      if (Array.isArray(value)) {
        console.log('→ 判断为扁平格式（有数组值）');
        return true;  // 有数组值，是扁平格式
      }
    }
    
    // 所有值都是对象，检查是否是“分组对象”格式
    // 如果所有值都是对象，且对象内的值是基本类型，则是混合格式（多个分组）
    const firstValue = data[keys[0]];
    console.log('firstValue:', firstValue);
    if (firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
      // 检查第一个对象内的值类型
      const innerKeys = Object.keys(firstValue);
      console.log('innerKeys:', innerKeys);
      if (innerKeys.length > 0) {
        const firstInnerValue = firstValue[innerKeys[0]];
        console.log('firstInnerValue:', firstInnerValue, 'type:', typeof firstInnerValue);
        // 如果内层值是基本类型，说明是分组对象格式，按扁平格式处理
        if (typeof firstInnerValue !== 'object' || firstInnerValue === null) {
          console.log('→ 判断为扁平格式（分组对象）');
          return true;  // 混合格式，按扁平处理
        }
      }
    }
    
    console.log('→ 判断为嵌套格式');
    // 默认：嵌套格式
    return false;
  };

  /**
   * 渲染数据字段（支持嵌套展示和扁平格式）
   * 第一个字段固定为“文件类型”
   * 支持两种格式：
   * 1. 嵌套格式：{"文件类型key": {"字段名": "值"}}
   * 2. 扁平格式：{"字段名": ["值"], "字段名2": ["值2"]}
   */
  const renderDataFields = (data, fileType, orderedFields, isEditable = false, onFieldChange = null, onFileTypeChange = null, onFieldDelete = null, onAddSubField = null) => {
    // 数据为空时的处理：如果有文件类型，仍然显示文件类型字段和新增字段按钮
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
      return (
        <div className="space-y-3">
          {/* 文件类型字段固定在第一位 */}
          <div className="flex items-center border-b pb-2">
            <span className="font-medium text-gray-700 w-32 flex-shrink-0">文件类型：</span>
            {isEditable ? (
              <Input
                value={fileType || ''}
                onChange={(e) => onFileTypeChange?.(e.target.value)}
                className="flex-1"
                placeholder="请输入文件类型"
              />
            ) : (
              <span className="text-gray-900">{fileType || '-'}</span>
            )}
          </div>
          
          {/* 空数据提示 */}
          <div className="text-gray-400 text-center py-4">暂无数据，请点击下方“新增字段”按钮添加字段</div>
        </div>
      );
    }
  
    // 判断是扁平格式还是嵌套格式
    const isFlat = isFlatFormat(data);
  
    if (isFlat) {
      // 扁平格式：{"字段名": ["值"], "字段名2": ["值2"]} 或 {"分组1": {"字段": "值"}}
      const flatFields = Object.keys(data);
  
      return (
        <div className="space-y-3">
          {/* 文件类型字段固定在第一位，可修改值但不可删除 */}
          <div className="flex items-center border-b pb-2">
            <span className="font-medium text-gray-700 w-32 flex-shrink-0">文件类型：</span>
            {isEditable ? (
              <Input
                value={fileType}
                onChange={(e) => onFileTypeChange?.(e.target.value)}
                className="flex-1"
                placeholder="请输入文件类型"
              />
            ) : (
              <span className="text-gray-900">{fileType || '-'}</span>
            )}
          </div>
  
          {/* 其他字段按顺序展示 */}
          {flatFields.map((fieldKey) => {
            const fieldValue = data[fieldKey];
            // 如果字段值是对象（嵌套格式），直接递归渲染
            if (fieldValue !== null && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
              return renderField(fieldKey, fieldValue, [], isEditable, onFieldChange, onFieldDelete, onAddSubField, 0);
            }
            // 普通字段
            return renderField(fieldKey, fieldValue, [], isEditable, onFieldChange, onFieldDelete, onAddSubField, 0);
          })}
        </div>
      );
    } else {
      // 嵌套格式：{"文件类型key": {"字段名": "值"}}
      const fileTypeKey = Object.keys(data)[0];
      const fieldsData = data[fileTypeKey] || {};
  
      return (
        <div className="space-y-3">
          {/* 文件类型字段固定在第一位，可修改值但不可删除 */}
          <div className="flex items-center border-b pb-2">
            <span className="font-medium text-gray-700 w-32 flex-shrink-0">文件类型：</span>
            {isEditable ? (
              <Input
                value={fileType || fileTypeKey}
                onChange={(e) => onFileTypeChange?.(e.target.value)}
                className="flex-1"
                placeholder="请输入文件类型"
              />
            ) : (
              <span className="text-gray-900">{fileType || fileTypeKey || '-'}</span>
            )}
          </div>
  
          {/* 其他字段按顺序展示（支持嵌套） */}
          {orderedFields.map((fieldKey) => {
            const fieldValue = fieldsData[fieldKey];
            return renderField(fieldKey, fieldValue, [fileTypeKey], isEditable, onFieldChange, onFieldDelete, onAddSubField, 0);
          })}
        </div>
      );
    }
  };

  /**
   * 获取文件预览URL
   * 文件预览链接格式要求：
   * 1. PDF文件：浏览器原生支持预览
   * 2. 图片文件(jpg/jpeg/png/gif)：使用 attachmentUrl 直接展示
   * 3. Office文件(doc/docx/xls/xlsx/ppt/pptx)：使用文件中心预览URL（attachmentUrl）
   * 4. 其他格式：提供下载链接
   *
   * 重要说明：
   * - 后端 /api/use-case/{id}/with-attachment 返回的 attachmentUrl 已经是文件中心预览URL
   * - 无需再调用 /api/use-case/preview-file 接口
   * - attachmentId 仅用于判断文件是否存在
   * - 加密场景下，attachmentUrl 为代理预览URL：/file/preview-attachment?attachmentId=xxx
   */
  const getFilePreviewUrl = (attachmentUrl, attachmentName, attachmentId) => {
    // attachmentId 用于判断文件是否存在，但预览直接使用 attachmentUrl（文件中心预览链接）
    if (!attachmentId || attachmentId === 'null' || attachmentId === null) {
      return null;
    }
    
    if (!attachmentUrl) {
      return null;
    }

    const fileName = attachmentName || '';
    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
    
    // 判断是否为加密场景（通过URL是否包含 /file/preview-attachment 来判断）
    const isEncrypted = attachmentUrl.includes('/file/preview-attachment');

    // 根据文件类型返回不同的预览方式
    // PDF 文件：浏览器原生支持预览
    if (fileExtension === 'pdf') {
      return { type: 'pdf', url: attachmentUrl, fileName: fileName, isEncrypted };
    }
    // 图片文件：使用 img 标签展示
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension)) {
      return { type: 'image', url: attachmentUrl, fileName: fileName, isEncrypted };
    }
    // Word 文件（.doc/.docx）：加密场景使用 docx-preview，非加密场景使用 iframe
    if (['doc', 'docx'].includes(fileExtension)) {
      // 仅在加密场景下使用 docx-preview
      if (isEncrypted) {
        return { type: 'word', url: attachmentUrl, fileName: fileName, isEncrypted: true };
      }
      // 非加密场景使用文件中心预览
      return { type: 'office', url: attachmentUrl, fileName: fileName, isEncrypted: false };
    }
    // 其他 Office 文件（Excel/PPT）：直接使用文件中心预览URL
    if (['xls', 'xlsx', 'ppt', 'pptx'].includes(fileExtension)) {
      return { type: 'office', url: attachmentUrl, fileName: fileName, isEncrypted };
    }
    // 其他格式：提供下载链接
    return { type: 'download', url: attachmentUrl, fileName: fileName, isEncrypted };
  };

  /**
   * Word文档预览组件（仅用于加密场景）
   * 使用 docx-preview 库在前端渲染 Word 文档
   */
  const WordPreview = ({ url, fileName }) => {
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const renderWord = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // 从后端获取解密后的 docx 文件流
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`文件加载失败: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          
          // 使用 docx-preview 渲染
          if (containerRef.current) {
            await renderAsync(blob, containerRef.current, null, {
              className: "docx-preview-content",
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false,
            });
          }
        } catch (err) {
          console.error('Word文档渲染失败:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      if (url) {
        renderWord();
      }
    }, [url]);

    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded">
          <Spin size="large" />
          <span className="text-gray-400 mt-4">正在加载 Word 文档...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded">
          <span className="text-red-500 mb-2">Word 文档加载失败</span>
          <span className="text-gray-400 text-sm mb-4">{error}</span>
          <Button type="primary" href={url} target="_blank">
            下载查看
          </Button>
        </div>
      );
    }

    return (
      <div 
        ref={containerRef} 
        className="w-full h-full overflow-auto bg-white rounded"
        style={{ padding: '20px' }}
      />
    );
  };

  // 渲染文件预览组件
  const renderFilePreview = () => {
    const attachmentUrl = useCaseWithAttachment?.attachmentUrl;
    const attachmentName = useCaseWithAttachment?.attachmentName;
    const attachmentId = useCaseWithAttachment?.attachmentId;
  
    // 当文件id不存在或为空时，或者为"null"字符串时，不要去请求接口预览
    if (!attachmentId || attachmentId === 'null' || attachmentId === null) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded">
          <span className="text-gray-400">暂不支持预览</span>
          {attachmentName && (
            <span className="text-gray-400 text-sm mt-2">{attachmentName}</span>
          )}
        </div>
      );
    }
  
    if (!attachmentUrl) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 rounded">
          <span className="text-gray-400">暂无附件</span>
        </div>
      );
    }
  
    // 传入 attachmentId 用于判断文件是否存在
    const previewInfo = getFilePreviewUrl(attachmentUrl, attachmentName, attachmentId);

    if (!previewInfo) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 rounded">
          <span className="text-gray-400">无法预览该文件</span>
        </div>
      );
    }

    switch (previewInfo.type) {
      case 'pdf':
        return (
          <iframe
            src={previewInfo.url}
            className="w-full h-full border-0 rounded"
            title="PDF预览"
          />
        );
      case 'image':
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded overflow-auto p-4">
            <img
              src={previewInfo.url}
              alt={attachmentName || '图片预览'}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(100% - 40px)' }}
              onError={(e) => {
                console.error('图片加载失败:', previewInfo.url);
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
            <div className="hidden flex-col items-center mt-4">
              <span className="text-gray-400 mb-2">图片加载失败</span>
              <Button type="primary" href={previewInfo.url} target="_blank">
                下载查看
              </Button>
            </div>
          </div>
        );
      case 'word':
        // Word 文档：仅在加密场景下使用 docx-preview
        return <WordPreview url={previewInfo.url} fileName={previewInfo.fileName} />;
      case 'office':
        // Office 文档直接使用文件中心预览URL（attachmentUrl 已经是预览链接）
        return (
          <iframe
            src={previewInfo.url}
            className="w-full h-full border-0 rounded"
            title="Office文档预览"
          />
        );
      case 'download':
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded">
            <span className="text-gray-500 mb-2">该文件类型不支持在线预览</span>
            {previewInfo.fileName && (
              <span className="text-gray-400 text-sm mb-4">{previewInfo.fileName}</span>
            )}
            <Button type="primary" href={previewInfo.url} target="_blank">
              下载文件
            </Button>
          </div>
        );
    }
  };
  
  // 打开Benchmark创建弹窗
  const handleCreateBenchmark = (executionRecord) => {
    setSelectedExecutionRecord(executionRecord);
    setBenchmarkModalVisible(true);
    
    // 生成默认的Benchmark版本号
    const now = new Date();
    const version = `V${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    
    // 从任务执行历史记录中获取业务场景信息
    // 需要通过taskId找到对应的任务，然后获取业务场景信息
    const task = dataSource.find(t => t.id === executionRecord.taskId);
    const businessScenarioName = task ? task.businessScenarioName : '';
    const businessScenarioId = task ? task.businessScenarioId : null;
    
    // 设置表单初始值
    benchmarkForm.setFieldsValue({
      benchmarkVersion: version,
      businessScenarioId: businessScenarioId,
      businessScenarioName: businessScenarioName,
      taskExecutionHistoryId: executionRecord.id,
      remark: ''
    });
  };
  
  // 保存Benchmark
  const handleSaveBenchmark = async () => {
    try {
      const values = await benchmarkForm.validateFields();
      
      // 构建提交数据
      const submitData = {
        benchmarkType: values.benchmarkType,
        benchmarkVersion: values.benchmarkVersion,
        businessScenarioId: values.businessScenarioId,
        taskExecutionHistoryId: values.taskExecutionHistoryId,
        remark: values.remark || '',
        useCaseCount: selectedExecutionRecord.totalCaseCount || 0
      };
      
      await benchmarkApi.create(submitData);
      message.success('Benchmark创建成功');
      setBenchmarkModalVisible(false);
      benchmarkForm.resetFields();
    } catch (error) {
      console.error('创建Benchmark失败:', error);
      message.error('创建Benchmark失败，请重试');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingRecord) {
        await judgeTaskApi.update(editingRecord.id, values);
        message.success('修改成功');
      } else {
        await judgeTaskApi.create(values);
        message.success('新增成功');
      }
      
      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('保存评测任务失败:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'PENDING': { color: 'default', text: '待执行' },
      'RUNNING': { color: 'processing', text: '执行中' },
      'SUCCESS': { color: 'success', text: '执行成功' },
      'FAILED': { color: 'error', text: '执行失败' },
      'CANCELLED': { color: 'warning', text: '已取消' }
    };
    const config = statusMap[status] || statusMap['PENDING'];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getTaskTypeColor = (code) => {
    const colorMap = {
      'BASELINE': 'blue',
      'ACCEPTANCE': 'green',
      'PERIODIC': 'orange',
      // 兼容旧数据中文值
      '基线评测': 'blue',
      '样本验收评测': 'green',
      '线上评测': 'orange',
    };
    return colorMap[code] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 200,
    },
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 120,
      render: (type) => (
        <Tag color={getTaskTypeColor(type)}>{getTaskTypeLabel(type)}</Tag>
      ),
    },
    {
      title: '业务场景',
      dataIndex: 'businessScenarioName',
      key: 'businessScenarioName',
      width: 180,
      render: (name) => (
        <Tag color="purple">{name}</Tag>
      ),
    },
    {
      title: '评测集',
      dataIndex: 'judgeSetName',
      key: 'judgeSetName',
      width: 180,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {(record.status === 'PENDING' || record.status === 'FAILED' ||  record.status === 'SUCCESS') && hasManagePermission() && (
            <Tooltip title="执行任务">
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleExecute(record.id)}
                style={{
                  background: '#E5F5FF',
                  border: '1px solid #91D5FF',
                  color: '#1890FF'
                }}
              >
                执行
              </Button>
            </Tooltip>
          )}
          <Tooltip title="执行历史">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record.id)}
              style={{
                background: '#E5F5FF',
                border: '1px solid #91D5FF',
                color: '#1890FF'
              }}
            >
              历史
            </Button>
          </Tooltip>
          {hasManagePermission() && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                background: '#E5F5FF',
                border: '1px solid #91D5FF',
                color: '#1890FF'
              }}
            >
              编辑
            </Button>
          )}
          {hasManagePermission() && (
            <Popconfirm
              title="确定要删除这个评测任务吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                className="table-action-btn-delete"
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">评测任务管理</h1>
        <p className="page-description">创建和管理评测任务，监控任务执行状态和结果</p>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <Space size="middle">
          <Search
            placeholder="请输入任务名称"
            allowClear
            suffix={<SearchOutlined />}
            size="large"
            className="w-80"
            onSearch={handleSearch}
          />
          <Select
            placeholder="任务类型"
            allowClear
            size="large"
            className="w-40"
            onChange={(value) => setSearchParams(prev => ({ ...prev, taskType: value }))}
          >
            {taskTypes.map(type => (
              <Option key={type.code} value={type.code}>{type.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="状态"
            allowClear
            size="large"
            className="w-32"
            onChange={(value) => setSearchParams(prev => ({ ...prev, status: value }))}
          >
            {taskStatuses.map(status => (
              <Option key={status} value={status}>
                {getStatusTag(status).props.children}
              </Option>
            ))}
          </Select>
        </Space>
        {hasManagePermission() && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
            style={{
              background: '#1890FF',
              borderColor: '#1890FF',
              boxShadow: 'none'
            }}
          >
            新增评测任务
          </Button>
        )}
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </div>

      <Modal
        title={editingRecord ? '编辑评测任务' : '新增评测任务'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnHidden
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="taskName"
            label="评测任务名称"
            rules={[
              { required: true, message: '请输入评测任务名称' },
              { max: 100, message: '评测任务名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入评测任务名称" />
          </Form.Item>
          
          <Form.Item
            name="taskType"
            label="任务类型"
            rules={[
              { required: true, message: '请选择任务类型' },
            ]}
          >
            <Select placeholder="请选择任务类型">
              {taskTypes.map(type => (
                <Option key={type.code} value={type.code}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="businessScenarioId"
            label="业务场景"
            rules={[
              { required: true, message: '请选择业务场景' },
            ]}
          >
            <Select placeholder="请选择业务场景" disabled>
              {currentScenario && (
                <Option key={currentScenario.id} value={currentScenario.id}>
                  {currentScenario.scenarioName}
                </Option>
              )}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="judgeSetId"
            label="评测集"
            rules={[
              { required: true, message: '请选择评测集' },
            ]}
          >
            <Select placeholder="请选择评测集">
              {judgeSets.map(set => (
                <Option key={set.id} value={set.id}>
                  {set.setName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="judgeScenarioId"
            label="评测场景"
            rules={[
              { required: true, message: '请选择评测场景' },
            ]}
          >
            <Select placeholder="请选择评测场景">
              {judgeScenarios.map(scenario => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.scenarioName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="hasExecutionResult"
            label="是否已有执行结果"
            rules={[
              { required: true, message: '请选择是否已有执行结果' },
            ]}
          >
            <Select placeholder="请选择是否已有执行结果">
              <Option value={0}>否</Option>
              <Option value={1}>是</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 任务执行历史页面 */}
      {historyModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 240,
          right: 0,
          bottom: 0,
          background: '#f5f5f5',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            flex: 1,
            overflow: 'auto'
          }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setHistoryModalVisible(false)}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{
                height: '24px',
                width: '1px',
                background: '#e8e8e8'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#000000'
              }}>
                任务执行历史
              </h2>
            </div>
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px 32px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              minHeight: 'calc(100vh - 140px)'
            }}>
              {/* 统计卡片 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  color: '#333',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>总执行次数</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, marginTop: '8px' }}>
                    {taskExecutionHistory.length}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  color: '#333',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>已完成</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, marginTop: '8px' }}>
                    {taskExecutionHistory.filter(h => h.executionStatus === 'COMPLETED').length}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  color: '#333',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>执行中</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, marginTop: '8px' }}>
                    {taskExecutionHistory.filter(h => h.executionStatus === 'RUNNING').length}
                  </div>
                </div>
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  color: '#333',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>总用例数</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, marginTop: '8px' }}>
                    {taskExecutionHistory.reduce((sum, h) => sum + (h.totalCaseCount || 0), 0)}
                  </div>
                </div>
              </div>

              {/* 表格 */}
              <Table
                columns={[
                  {
                    title: '执行ID',
                    dataIndex: 'id',
                    key: 'id',
                    width: 80,
                  },
                  {
                    title: '任务名称',
                    dataIndex: 'taskName',
                    key: 'taskName',
                    width: 200,
                  },
                  {
                    title: '执行状态',
                    dataIndex: 'executionStatus',
                    key: 'executionStatus',
                    width: 100,
                    render: (status) => {
                      const statusMap = {
                        'RUNNING': { color: 'processing', text: '执行中' },
                        'COMPLETED': { color: 'success', text: '已完成' }
                      };
                      const config = statusMap[status] || { color: 'default', text: status };
                      return <Tag color={config.color} style={{ borderRadius: '4px' }}>{config.text}</Tag>;
                    }
                  },
                  {
                    title: '开始时间',
                    dataIndex: 'startTime',
                    key: 'startTime',
                    width: 150,
                    render: (time) => formatDateTime(time),
                  },
                  {
                    title: '结束时间',
                    dataIndex: 'endTime',
                    key: 'endTime',
                    width: 150,
                    render: (time) => formatDateTime(time),
                  },
                  {
                    title: '执行耗时',
                    dataIndex: 'duration',
                    key: 'duration',
                    width: 70,
                    render: (duration) => duration ? `${duration}s` : '-',
                  },
                  {
                    title: '用例统计',
                    key: 'caseStats',
                    width: 84,
                    render: (_, record) => (
                      <div className="text-xs">
                        <div>总: {record.totalCaseCount || 0}</div>
                        <div className="text-green-600">成功: {record.successCaseCount || 0}</div>
                        <div className="text-red-600">失败: {record.failedCaseCount || 0}</div>
                      </div>
                    ),
                  },
                  // 要素抽取场景才展示文件准确率和字段准确率（机审场景不展示）
                  ...((currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION') ? [
                    {
                      title: '文件准确率',
                      dataIndex: 'filePassRate',
                      key: 'filePassRate',
                      width: 100,
                      render: (rate) => rate || '-',
                    },
                    {
                      title: '字段准确率',
                      dataIndex: 'fieldPassRate',
                      key: 'fieldPassRate',
                      width: 100,
                      render: (rate) => rate || '-',
                    }
                  ] : []),
                  {
                    title: '操作',
                    key: 'action',
                    width: 360,
                    fixed: 'right',
                    render: (_, record) => {
                      // 构建所有按钮
                      const allButtons = [
                        {
                          key: 'detail',
                          label: '执行详情',
                          icon: <EyeOutlined />,
                          onClick: () => handleViewCaseHistory(record)
                        },
                        {
                          key: 'stats',
                          label: '结果统计',
                          icon: <BarChartOutlined />,
                          onClick: () => handleViewResultStats(record)
                        },
                        {
                          key: 'recalculate',
                          label: '重新计算',
                          icon: <RedoOutlined />,
                          onClick: () => handleRecalculate(record)
                        },
                        // 要素抽取场景和机审场景显示Benchmark按钮
                        ...(['FEATURE_EXTRACTION', 'AUTO_REVIEW'].includes(currentScenario?.scenarioCategory) ? [{
                          key: 'benchmark',
                          label: 'Benchmark',
                          icon: <TrophyOutlined />,
                          onClick: () => handleCreateBenchmark(record)
                        }] : []),
                        {
                          key: 'export',
                          label: '导出',
                          icon: <ExportOutlined />,
                          onClick: () => handleExportHistory(record)
                        }
                      ];

                      // 最多展示3个按钮
                      const maxVisibleButtons = 3;
                      const visibleButtons = allButtons.slice(0, maxVisibleButtons);
                      const hiddenButtons = allButtons.slice(maxVisibleButtons);

                      return (
                        <Space size="small">
                          {visibleButtons.map(btn => (
                            <Button
                              key={btn.key}
                              size="small"
                              icon={btn.icon}
                              onClick={btn.onClick}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#1890FF',
                                padding: '0 8px'
                              }}
                            >
                              {btn.label}
                            </Button>
                          ))}
                          {hiddenButtons.length > 0 && (
                            <Dropdown
                              menu={{
                                items: hiddenButtons.map(btn => ({
                                  key: btn.key,
                                  icon: btn.icon,
                                  label: btn.label,
                                  onClick: btn.onClick
                                }))
                              }}
                            >
                              <Button
                                size="small"
                                icon={<DownOutlined />}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#1890FF',
                                  padding: '0 8px'
                                }}
                              >
                                更多
                              </Button>
                            </Dropdown>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
                dataSource={taskExecutionHistory}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
                style={{ borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 用例执行详情页面 */}
      {caseHistoryModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 240,
          right: 0,
          bottom: 0,
          background: '#f5f5f5',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            flex: 1,
            overflow: 'auto'
          }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  setCaseHistoryModalVisible(false);
                  setRerunningCaseIds(new Set());
                }}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{
                height: '24px',
                width: '1px',
                background: '#e8e8e8'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#000000'
              }}>
                用例执行详情
              </h2>
            </div>
            
            {/* 机审场景显示下载报告和回传报告按钮 */}
            {currentScenario?.scenarioCategory === 'AUTO_REVIEW' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  onClick={() => handleDownloadReport()}
                  style={{
                    background: '#F0F5FF',
                    border: '1px solid #ADC6FF',
                    color: '#2F54EB',
                    fontWeight: 500
                  }}
                >
                  下载报告
                </Button>
                <Button
                  onClick={() => handleUploadReport()}
                  style={{
                    background: '#FFF7E6',
                    border: '1px solid #FFD591',
                    color: '#FA8C16',
                    fontWeight: 500
                  }}
                >
                  回传报告
                </Button>
              </div>
            )}
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px 32px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              minHeight: 'calc(100vh - 140px)'
            }}>
              {/* 筛选条件 */}
              {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
                <div style={{
                  marginBottom: '24px',
                  padding: '16px 20px',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontWeight: 500, color: '#333' }}>筛选条件：</span>
                  <Select
                    placeholder="用例名称"
                    allowClear
                    showSearch
                    style={{ width: 180, borderRadius: '8px' }}
                    value={caseNameFilter}
                    onChange={(val) => {
                      setCaseNameFilter(val);
                      setCasePagination(prev => ({ ...prev, current: 1 }));
                    }}
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {[...new Set(caseExecutionHistory.map(item => item.caseName).filter(Boolean))].map(name => (
                      <Option key={name} value={name}>{name}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="执行状态"
                    allowClear
                    style={{ width: 140, borderRadius: '8px' }}
                    value={caseExecutionStatusFilter}
                    onChange={(val) => {
                      setCaseExecutionStatusFilter(val);
                      setCasePagination(prev => ({ ...prev, current: 1 }));
                    }}
                  >
                    <Option value="PENDING">待执行</Option>
                    <Option value="RUNNING">执行中</Option>
                    <Option value="SUCCESS">执行成功</Option>
                    <Option value="FAILED">执行失败</Option>
                    <Option value="CANCELLED">已取消</Option>
                  </Select>
                  {/* ALE执行状态筛选（要素抽取场景） */}
                  {currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
                    <Select
                      placeholder="ALE执行状态"
                      allowClear
                      style={{ width: 140, borderRadius: '8px' }}
                      value={caseAleTaskStatusFilter}
                      onChange={(val) => {
                        setCaseAleTaskStatusFilter(val);
                        setCasePagination(prev => ({ ...prev, current: 1 }));
                      }}
                    >
                      <Option value="DONE">成功</Option>
                      <Option value="FAILED">失败</Option>
                      <Option value="ERROR">错误</Option>
                      <Option value="CANCELED">取消</Option>
                    </Select>
                  )}
                  {/* 机审执行状态筛选（机审场景） */}
                  {currentScenario?.scenarioCategory === 'AUTO_REVIEW' && (
                    <Select
                      placeholder="机审执行状态"
                      allowClear
                      style={{ width: 140, borderRadius: '8px' }}
                      value={caseAleTaskStatusFilter}
                      onChange={(val) => {
                        setCaseAleTaskStatusFilter(val);
                        setCasePagination(prev => ({ ...prev, current: 1 }));
                      }}
                    >
                      <Option value="success">执行成功</Option>
                      <Option value="overTime">超时</Option>
                      <Option value="fail">失败</Option>
                    </Select>
                  )}
                  <Select
                    placeholder="断言结果"
                    allowClear
                    style={{ width: 140, borderRadius: '8px' }}
                    value={caseAssertionResultFilter}
                    onChange={(val) => {
                      setCaseAssertionResultFilter(val);
                      setCasePagination(prev => ({ ...prev, current: 1 }));
                    }}
                  >
                    <Option value="passed">通过</Option>
                    <Option value="failed">失败</Option>
                  </Select>
                  {currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
                    <Select
                      placeholder="文件类型"
                      allowClear
                      style={{ width: 180, borderRadius: '8px' }}
                      value={caseAttachmentTypeFilter}
                      onChange={(val) => {
                        setCaseAttachmentTypeFilter(val);
                        setCasePagination(prev => ({ ...prev, current: 1 }));
                      }}
                    >
                      {attachmentTypeOptions.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.text}</Option>
                      ))}
                    </Select>
                  )}
                  {/* 选择所有 和 整体重跑按钮 */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        const newVal = !isSelectAllCases;
                        setIsSelectAllCases(newVal);
                        if (!newVal) {
                          setSelectedCaseRowKeys([]);
                          setSelectedCaseRecords([]);
                        }
                      }}
                      style={{
                        background: isSelectAllCases ? '#E6F7FF' : '#fff',
                        border: isSelectAllCases ? '1px solid #1890FF' : '1px solid #d9d9d9',
                        color: isSelectAllCases ? '#1890FF' : '#595959',
                        fontWeight: 500
                      }}
                    >
                      {isSelectAllCases ? `已选所有 (${casePagination.total}条)` : '选择所有'}
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      loading={batchRerunLoading}
                      disabled={!isSelectAllCases && selectedCaseRowKeys.length === 0}
                      onClick={handleBatchRerun}
                      style={{
                        fontWeight: 500
                      }}
                    >
                      整体重跑{(isSelectAllCases || selectedCaseRowKeys.length > 0) ? ` (${isSelectAllCases ? casePagination.total : selectedCaseRowKeys.length})` : ''}
                    </Button>
                  </div>
                </div>
              )}

              {/* 统计卡片 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ fontSize: '13px', color: '#666' }}>总用例数</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '4px', color: '#333' }}>
                    {selectedHistoryRecord?.totalCaseCount || 0}
                  </div>
                </div>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ fontSize: '13px', color: '#666' }}>断言通过</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '4px', color: '#52c41a' }}>
                    {/* 优先使用接口返回的数据（用例数超过页大小时），否则使用本地计算 */}
                    {passRateStats ? passRateStats.passCount : assertionStats.passed}
                  </div>
                </div>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ fontSize: '13px', color: '#666' }}>断言失败</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '4px', color: '#ff4d4f' }}>
                    {/* 优先使用接口返回的数据（用例数超过页大小时），否则使用本地计算 */}
                    {passRateStats ? passRateStats.failCount : assertionStats.failed}
                  </div>
                </div>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ fontSize: '13px', color: '#666' }}>断言通过率</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '4px', color: '#1890ff' }}>
                    {(() => {
                      // 优先使用接口返回的通过率（用例数超过页大小时）
                      if (passRateStats?.passRate) {
                        return passRateStats.passRate;
                      }
                      // 否则使用本地计算
                      const passed = assertionStats.passed;
                      const total = selectedHistoryRecord?.totalCaseCount || 0;
                      return total > 0 ? `${Math.round(passed / total * 100)}%` : '-';
                    })()}
                  </div>
                </div>
              </div>

              {/* 表格 */}
              {(() => {
                const filteredData = caseExecutionHistory.filter(item => {
                  // 1. 文件类型筛选（本地）
                  if (caseAttachmentTypeFilter && item.attachmentType !== caseAttachmentTypeFilter) {
                    return false;
                  }
                  // 2. 断言结果筛选（本地）
                  if (caseAssertionResultFilter) {
                    try {
                      const result = typeof item.assertionResult === 'string'
                        ? JSON.parse(item.assertionResult)
                        : item.assertionResult;
                      const passed = !!result?.passed;
                      if (caseAssertionResultFilter === 'passed' && !passed) return false;
                      if (caseAssertionResultFilter === 'failed' && passed) return false;
                    } catch (e) {
                      return false;
                    }
                  }
                  // 3. ALE执行状态筛选（本地）
                  if (caseAleTaskStatusFilter && item.aleTaskStatus !== caseAleTaskStatusFilter) {
                    return false;
                  }
                  return true;
                });

                return (
                  <Table
                    rowSelection={{
                      selectedRowKeys: isSelectAllCases ? filteredData.map(item => item.id) : selectedCaseRowKeys,
                      onChange: (newSelectedRowKeys, newSelectedRows) => {
                        if (!isSelectAllCases) {
                          setSelectedCaseRowKeys(prev => {
                            // 合并分页选择：保留其他页已选的，更新当前页选中的
                            const currentPageIds = new Set(filteredData.map(item => item.id));
                            const otherPageSelected = prev.filter(id => !currentPageIds.has(id));
                            return [...otherPageSelected, ...newSelectedRowKeys];
                          });
                          setSelectedCaseRecords(prev => {
                            const currentPageIds = new Set(filteredData.map(item => item.id));
                            const otherPageSelected = prev.filter(r => !currentPageIds.has(r.id));
                            return [...otherPageSelected, ...newSelectedRows];
                          });
                        }
                      },
                      getCheckboxProps: (record) => ({
                        disabled: isSelectAllCases,
                      }),
                    }}
                    columns={[
                      {
                        title: '执行ID',
                        dataIndex: 'id',
                        key: 'id',
                        width: 60,
                      },
                      {
                        title: '用例名称',
                        dataIndex: 'caseName',
                        key: 'caseName',
                        width: 130,
                      },
                      ...(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' ? [
                        {
                          title: '文件类型',
                          dataIndex: 'attachmentType',
                          key: 'attachmentType',
                          width: 100,
                          render: (type) => type || '-',
                        }
                      ] : []),
                      {
                        title: '执行状态',
                        dataIndex: 'executionStatus',
                        key: 'executionStatus',
                        width: 80,
                        render: (status, record) => {
                          const displayStatus = rerunningCaseIds.has(record.id) ? 'RUNNING' : status;
                          return getStatusTag(displayStatus);
                        },
                        ...((currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? {
                          filteredValue: caseExecutionStatusFilter ? [caseExecutionStatusFilter] : null,
                          onFilter: (value, record) => record.executionStatus === value,
                        } : {}),
                      },
                      ...((currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION') ? [
                        {
                          title: 'ALE执行状态',
                          dataIndex: 'aleTaskStatus',
                          key: 'aleTaskStatus',
                          width: 100,
                          render: (status) => {
                            const statusMap = {
                              'DONE': { color: 'success', text: '成功' },
                              'FAILED': { color: 'error', text: '失败' },
                              'ERROR': { color: 'warning', text: '错误' },
                              'CANCELED': { color: 'default', text: '取消' },
                              'RUNNING': { color: 'processing', text: '执行中' },
                            };
                            const config = statusMap[status] || { color: 'default', text: status || '-' };
                            return <Tag color={config.color} style={{ borderRadius: '4px' }}>{config.text}</Tag>;
                          },
                        }
                      ] : []),
                      ...((currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? [
                        {
                          title: '机审执行状态',
                          dataIndex: 'aleTaskStatus',
                          key: 'aleTaskStatus',
                          width: 110,
                          render: (status) => {
                            const statusMap = {
                              'success': { color: 'success', text: '执行成功' },
                              'overTime': { color: 'warning', text: '超时' },
                              'fail': { color: 'error', text: '失败' },
                              'RUNNING': { color: 'processing', text: '执行中' },
                            };
                            const config = statusMap[status] || { color: 'default', text: status || '-' };
                            return <Tag color={config.color} style={{ borderRadius: '4px' }}>{config.text}</Tag>;
                          },
                        }
                      ] : []),
                      {
                        title: '开始时间',
                        dataIndex: 'startTime',
                        key: 'startTime',
                        width: 150,
                        render: (time) => formatDateTime(time),
                      },
                      {
                        title: '结束时间',
                        dataIndex: 'endTime',
                        key: 'endTime',
                        width: 150,
                        render: (time) => formatDateTime(time),
                      },
                      {
                        title: '执行耗时',
                        dataIndex: 'duration',
                        key: 'duration',
                        width: 70,
                        render: (duration) => duration ? `${duration}s` : '-',
                      },
                      {
                        title: '执行结果/错误信息',
                        dataIndex: 'executionResultData',
                        key: 'executionResult',
                        width: 300,
                        ellipsis: true,
                        render: (executionResultData, record) => {
                          if (record.executionStatus === 'SUCCESS') {
                            return executionResultData || '-';
                          } else if (record.executionStatus === 'FAILED') {
                            return (
                              <span className="text-red-600">
                                {record.errorMessage || '-'}
                              </span>
                            );
                          }
                          return '-';
                        },
                      },
                      {
                        title: '断言结果',
                        dataIndex: 'assertionResult',
                        key: 'assertionResult',
                        width: 100,
                        ellipsis: true,
                        render: (assertionResult) => {
                          if (!assertionResult) return '-';

                          try {
                            const result = typeof assertionResult === 'string'
                              ? JSON.parse(assertionResult)
                              : assertionResult;

                            return (
                              <div>
                                <div>
                                  {result.passed ? (
                                    <span className="text-green-600">✓ {result.message || '断言通过'}</span>
                                  ) : (
                                    <span className="text-red-600">✗ {result.message || '断言失败'}</span>
                                  )}
                                </div>
                                {result.details && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {result.details.error || ''}
                                  </div>
                                )}
                              </div>
                            );
                          } catch (e) {
                            return assertionResult;
                          }
                        },
                      },
                      // 机审场景不展示差异字段
                      ...((currentScenario?.scenarioCategory !== 'AUTO_REVIEW') ? [
                        {
                          title: '差异字段',
                          dataIndex: 'assertionResult',
                          key: 'diffFields',
                          width: 120,
                          ellipsis: true,
                          render: (assertionResult) => {
                            if (!assertionResult) return '-';

                            try {
                              const result = typeof assertionResult === 'string'
                                ? JSON.parse(assertionResult)
                                : assertionResult;

                              // 断言通过时不展示
                              if (result.passed) return '-';

                              // 检查details是否存在且为有效数组
                              if (!result.details || !Array.isArray(result.details) || result.details.length === 0) {
                                return '-';
                              }

                              // 提取所有field字段，用逗号隔开
                              const fields = result.details
                                .map(item => item?.field)
                                .filter(field => field)
                                .join(', ');

                              return fields ? (
                                <Tooltip title={fields}>
                                  <span className="text-orange-500">{fields}</span>
                                </Tooltip>
                              ) : '-';
                            } catch (e) {
                              return '-';
                            }
                          },
                        }
                      ] : []),
                      ...((currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION') ? [
                        {
                          title: '操作',
                          key: 'action',
                          width: 200,
                          fixed: 'right',
                          render: (_, record) => {
                            // 解析断言结果
                            let showDiffButton = false;
                            let diffDetails = [];
                            try {
                              const assertionResult = record.assertionResult;
                              if (assertionResult) {
                                const result = typeof assertionResult === 'string'
                                  ? JSON.parse(assertionResult)
                                  : assertionResult;
                                // 只有断言不通过且details有效时才显示按钮
                                if (!result.passed && result.details && Array.isArray(result.details) && result.details.length > 0) {
                                  showDiffButton = true;
                                  diffDetails = result.details;
                                }
                              }
                            } catch (e) {
                              // 解析失败不显示按钮
                            }

                            const isRunning = record.executionStatus === 'RUNNING' || rerunningCaseIds.has(record.id);

                            return (
                              <Space size={4}>
                                {showDiffButton && (
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setCurrentDiffDetails(diffDetails);
                                      setCurrentDiffCaseName(record.caseName || `用例${record.id}`);
                                      setDiffDrawerVisible(true);
                                    }}
                                    style={{
                                      background: '#FFF7E6',
                                      border: '1px solid #FFD591',
                                      color: '#FA8C16',
                                      fontWeight: 500
                                    }}
                                  >
                                    查看差异
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  onClick={() => handleAnnotateCase(record)}
                                  style={{
                                    background: '#E5F5FF',
                                    border: '1px solid #91D5FF',
                                    color: '#1890FF',
                                    fontWeight: 500
                                  }}
                                >
                                  标注
                                </Button>
                                <Button
                                  size="small"
                                  disabled={isRunning}
                                  onClick={() => handleRerunCaseWithConfirm(record)}
                                  style={{
                                    background: isRunning ? undefined : '#F6FFED',
                                    border: isRunning ? undefined : '1px solid #B7EB8F',
                                    color: isRunning ? undefined : '#52C41A',
                                    fontWeight: 500
                                  }}
                                >
                                  重跑
                                </Button>
                              </Space>
                            );
                          },
                        }
                      ] : []),
                      ...((currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? [
                        {
                          title: '操作',
                          key: 'action',
                          width: 250,
                          fixed: 'right',
                          render: (_, record) => {
                            // 判断是否显示跳转打标按钮
                            const showMarkingButton = currentJudgeScenario?.hasIndependentPlatform === 'y';
                            const isRunning = record.executionStatus === 'RUNNING' || rerunningCaseIds.has(record.id);

                            return (
                              <Space size={4}>
                                <Button
                                  size="small"
                                  onClick={() => handleViewAnnotationDetail(record)}
                                  style={{
                                    background: '#E5F5FF',
                                    border: '1px solid #91D5FF',
                                    color: '#1890FF',
                                    fontWeight: 500
                                  }}
                                >
                                  查看标注详情
                                </Button>
                                {showMarkingButton && (
                                  <Button
                                    size="small"
                                    onClick={() => handleMarkingRedirect(record)}
                                    style={{
                                      background: '#F6FFED',
                                      border: '1px solid #B7EB8F',
                                      color: '#52C41A',
                                      fontWeight: 500
                                    }}
                                  >
                                    跳转打标
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  disabled={isRunning}
                                  onClick={() => handleRerunCaseWithConfirm(record)}
                                  style={{
                                    background: isRunning ? undefined : '#FFF7E6',
                                    border: isRunning ? undefined : '1px solid #FFD591',
                                    color: isRunning ? undefined : '#FA8C16',
                                    fontWeight: 500
                                  }}
                                >
                                  重跑
                                </Button>
                              </Space>
                            );
                          },
                        }
                      ] : []),
                    ]}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={caseLoading}
                    pagination={{
                      ...casePagination,
                      total: casePagination.total, // 使用服务端返回的真实总数
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                      showQuickJumper: true,
                    }}
                    onChange={(newPagination) => setCasePagination(newPagination)}
                    size="small"
                    scroll={{ x: 1140, y: 'calc(100vh - 450px)' }}
                    style={{ borderRadius: '8px' }}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 查看差异抽屉 */}
      <Drawer
        title={null}
        placement="right"
        open={diffDrawerVisible}
        onClose={() => {
          setDiffDrawerVisible(false);
          setCurrentDiffDetails([]);
          setCurrentDiffCaseName('');
        }}
        width={600}
        destroyOnClose
        styles={{
          header: { display: 'none' },
          body: { padding: 0 }
        }}
      >
        <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setDiffDrawerVisible(false)}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{ height: '24px', width: '1px', background: '#e8e8e8' }} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
                字段差异详情
              </h2>
              {currentDiffCaseName && (
                <Tooltip title={currentDiffCaseName}>
                  <Tag color="orange" style={{ 
                    fontSize: '13px', 
                    padding: '2px 12px',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {currentDiffCaseName}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px' }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e8e8e8'
            }}>
              <Table
                dataSource={currentDiffDetails.map((item, index) => {
                  const formatDiffValue = (value) => {
                    if (value === null || value === undefined) return '-';
                    if (Array.isArray(value)) return JSON.stringify(value, null, 2);
                    if (typeof value === 'object') return JSON.stringify(value, null, 2);
                    return String(value);
                  };
                  return {
                    key: index,
                    field: item?.field || '-',
                    expected: formatDiffValue(item?.expected),
                    actual: formatDiffValue(item?.actual),
                  };
                })}
                columns={[
                  {
                    title: '字段名',
                    dataIndex: 'field',
                    key: 'field',
                    width: 150,
                    render: (text) => (
                      <span style={{ fontWeight: 600, color: '#262626' }}>
                        {text}
                      </span>
                    ),
                  },
                  {
                    title: '期望值',
                    dataIndex: 'expected',
                    key: 'expected',
                    render: (text) => (
                      <div style={{
                        color: '#262626',
                        wordBreak: 'break-word',
                        fontFamily: 'Monaco, Consolas, monospace',
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6'
                      }}>
                        {text}
                      </div>
                    ),
                  },
                  {
                    title: '实际值',
                    dataIndex: 'actual',
                    key: 'actual',
                    render: (text, record) => {
                      const isDifferent = record.expected !== record.actual;
                      return (
                        <div style={{
                          color: isDifferent ? '#ff4d4f' : '#262626',
                          wordBreak: 'break-word',
                          fontFamily: 'Monaco, Consolas, monospace',
                          fontSize: '13px',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6',
                          fontWeight: isDifferent ? 600 : 400
                        }}>
                          {text}
                        </div>
                      );
                    },
                  },
                ]}
                pagination={false}
                locale={{ emptyText: '暂无差异数据' }}
                scroll={{ y: 'calc(100vh - 180px)' }}
                rowClassName={(record) => 
                  record.expected !== record.actual ? 'diff-row-highlight' : ''
                }
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* Benchmark创建弹窗 */}
      <Modal
        title="创建Benchmark"
        open={benchmarkModalVisible}
        onOk={handleSaveBenchmark}
        onCancel={() => {
          setBenchmarkModalVisible(false);
          benchmarkForm.resetFields();
        }}
        width={600}
        destroyOnClose
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={benchmarkForm}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="benchmarkType"
            label="Benchmark类型"
            rules={[
              { required: true, message: '请选择Benchmark类型' },
            ]}
          >
            <Select placeholder="请选择Benchmark类型">
              {benchmarkTypes.map(type => (
                <Select.Option key={type.code} value={type.code}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="benchmarkVersion"
            label="Benchmark版本"
            rules={[
              { required: true, message: '请输入Benchmark版本' },
            ]}
          >
            <Input placeholder="请输入Benchmark版本" />
          </Form.Item>

          <Form.Item
            name="businessScenarioName"
            label="业务场景"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注信息"
          >
            <Input.TextArea
              rows={4}
              placeholder="请输入备注信息（可选）"
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* 隐藏字段 */}
          <Form.Item name="businessScenarioId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="taskExecutionHistoryId" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 标注抽屉 */}
      <Drawer
        title={null}
        placement="right"
        open={annotationModalVisible}
        onClose={handleCloseAnnotationModal}
        width="100%"
        destroyOnClose
        styles={{
          header: { display: 'none' },
          body: { padding: 0 }
        }}
      >
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 14.28%, #FFFBE5 28.56%, #F0FFE5 42.84%, #E5F5FF 57.12%, #EBE5FF 71.4%, #F5E5FF 85.68%, #FFE5F5 100%)'
        }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleCloseAnnotationModal}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{
                height: '24px',
                width: '1px',
                background: '#e8e8e8'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#000'
              }}>
                标注 - {currentAnnotationCase?.caseName || ''}
              </h2>
            </div>
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px 32px', height: 'calc(100vh - 80px)' }}>
            {annotationLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.98)',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
              }}>
                <span style={{ color: '#666', fontSize: '16px' }}>加载中...</span>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                gap: '24px',
                height: '100%'
              }}>
                {/* 左侧：文件预览区域 */}
                <div style={{
                  width: '55%',
                  flexShrink: 0,
                  minWidth: 0,
                  background: 'rgba(255, 255, 255, 0.98)',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    marginBottom: '16px',
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '4px',
                      height: '20px',
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #48dbfb 100%)',
                      borderRadius: '2px'
                    }} />
                    文件预览
                    {useCaseWithAttachment?.attachmentName && (
                      <span style={{ fontSize: '13px', color: '#999', fontWeight: 400 }}>
                        ({useCaseWithAttachment.attachmentName})
                      </span>
                    )}
                  </div>
                  <div style={{
                    flex: 1,
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid #e8e8e8',
                    overflow: 'hidden'
                  }}>
                    {renderFilePreview()}
                  </div>
                </div>

                {/* 右侧：接口返回数据和标准答案 */}
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {/* 接口返回数据（只读） */}
                  <div style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.98)',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      marginBottom: '12px',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        width: '4px',
                        height: '20px',
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #48dbfb 100%)',
                        borderRadius: '2px'
                      }} />
                      接口返回数据
                      <span style={{
                        fontSize: '12px',
                        color: '#999',
                        fontWeight: 400,
                        background: 'rgba(0, 0, 0, 0.05)',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>只读</span>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      {(() => {
                        // 接口返回数据的文件类型从解析后的结果中取值（第一层 key 就是文件类型）
                        const aleFileTypeKey = editableAleResultData ? Object.keys(editableAleResultData)[0] : null;
                        const aleFields = aleFileTypeKey && editableAleResultData[aleFileTypeKey] ? Object.keys(editableAleResultData[aleFileTypeKey]) : [];

                        return renderDataFields(
                          editableAleResultData,
                          aleFileTypeKey,
                          aleFields,
                          false,
                          null
                        );
                      })()}
                    </div>
                  </div>

                  {/* 标准答案（可编辑，文件类型除外） */}
                  <div style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.98)',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      marginBottom: '12px',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        width: '4px',
                        height: '20px',
                        background: 'linear-gradient(135deg, #48dbfb 0%, #1dd1a1 50%, #5f27cd 100%)',
                        borderRadius: '2px'
                      }} />
                      标准答案
                      <span style={{
                        fontSize: '12px',
                        color: '#52c41a',
                        fontWeight: 400,
                        background: 'rgba(82, 196, 26, 0.1)',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>可编辑</span>
                    </div>
                    {/* 文件类型字段 */}
                    <div style={{ marginBottom: '12px' }}>
                      <div className="flex items-center border-b pb-2">
                        <span className="font-medium text-gray-700 w-32 flex-shrink-0">文件类型：</span>
                        <Input
                          value={editableAttachmentType || ''}
                          onChange={(e) => setEditableAttachmentType(e.target.value)}
                          className="flex-1"
                          placeholder="请输入文件类型"
                        />
                      </div>
                    </div>
                    {/* 树状字段编辑器 */}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <TreeFieldEditor
                        data={rawExpectedAnswerForTree}
                        onChange={(serializedData) => {
                          // 将TreeFieldEditor输出的数据存入editableExpectedAnswerData
                          // 同时将originalExpectedAnswerFormat设为null，保存时跳过denormalize
                          setEditableExpectedAnswerData(serializedData || {});
                          setOriginalExpectedAnswerFormat(null);
                        }}
                        readOnly={!hasManagePermission()}
                      />
                    </div>
                    {/* 按钮区域 - 放置在数据下方 */}
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e8e8e8',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '12px'
                    }}>
                      {hasManagePermission() && (
                        <Button
                          onClick={handleSyncExpectedAnswer}
                          loading={annotationSaving}
                          style={{
                            background: '#1890FF',
                            borderColor: '#1890FF',
                            color: '#fff',
                            fontWeight: 500,
                            height: '36px',
                            padding: '0 20px',
                            borderRadius: '8px',
                            boxShadow: 'none'
                          }}
                        >
                          同步标准答案
                        </Button>
                      )}
                      {hasManagePermission() && (
                        <Button
                          type="primary"
                          onClick={handleSaveAnnotation}
                          loading={annotationSaving}
                          style={{
                            background: '#1890FF',
                            borderColor: '#1890FF',
                            fontWeight: 500,
                            height: '36px',
                            padding: '0 20px',
                            borderRadius: '8px',
                            boxShadow: 'none'
                          }}
                        >
                          保存标注
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer>

      {/* 标注详情弹窗（机审场景） */}
      <Modal
        title={null}
        open={annotationDetailVisible}
        onCancel={handleCloseAnnotationDetail}
        footer={null}
        width={1000}
        destroyOnClose
        styles={{
          body: { padding: 0 }
        }}
      >
        <div style={{ minHeight: '500px' }}>
          {/* 标题区域 */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>标注详情</span>
            {currentAnnotationDetailRecord?.caseName && (
              <Tag color="processing" style={{ borderRadius: '4px' }}>
                {currentAnnotationDetailRecord.caseName}
              </Tag>
            )}
          </div>

          {annotationDetailLoading ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <span style={{ color: '#999' }}>加载中...</span>
            </div>
          ) : (
            <>
              {/* 基本信息区域 */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fafafa'
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                  <div style={{ minWidth: '200px' }}>
                    <span style={{ color: '#666', fontSize: '13px' }}>用例名称</span>
                    <div style={{ marginTop: '4px', fontWeight: 500 }}>
                      {currentAnnotationDetailRecord?.caseName || '-'}
                    </div>
                  </div>
                  <div style={{ minWidth: '150px' }}>
                    <span style={{ color: '#666', fontSize: '13px' }}>用例单据ID</span>
                    <div style={{ marginTop: '4px', fontWeight: 500 }}>
                      {currentAnnotationDetailRecord?.documentId || '-'}
                    </div>
                  </div>
                  <div style={{ minWidth: '100px' }}>
                    <span style={{ color: '#666', fontSize: '13px' }}>执行状态</span>
                    <div style={{ marginTop: '4px' }}>
                      {currentAnnotationDetailRecord?.executionStatus === 'SUCCESS' ? (
                        <Tag color="success" style={{ borderRadius: '4px' }}>成功</Tag>
                      ) : currentAnnotationDetailRecord?.executionStatus === 'FAILED' ? (
                        <Tag color="error" style={{ borderRadius: '4px' }}>失败</Tag>
                      ) : currentAnnotationDetailRecord?.executionStatus === 'RUNNING' ? (
                        <Tag color="processing" style={{ borderRadius: '4px' }}>执行中</Tag>
                      ) : (
                        <Tag style={{ borderRadius: '4px' }}>{currentAnnotationDetailRecord?.executionStatus || '-'}</Tag>
                      )}
                    </div>
                  </div>
                  <div style={{ minWidth: '100px' }}>
                    <span style={{ color: '#666', fontSize: '13px' }}>断言结果</span>
                    <div style={{ marginTop: '4px' }}>
                      {(() => {
                        try {
                          const result = typeof currentAnnotationDetailRecord?.assertionResult === 'string'
                            ? JSON.parse(currentAnnotationDetailRecord.assertionResult)
                            : currentAnnotationDetailRecord?.assertionResult;
                          return result?.passed ? (
                            <Tag color="success" style={{ borderRadius: '4px' }}>通过</Tag>
                          ) : (
                            <Tag color="error" style={{ borderRadius: '4px' }}>未通过</Tag>
                          );
                        } catch (e) {
                          return <Tag style={{ borderRadius: '4px' }}>-</Tag>;
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* 单据标注结果区域 */}
              <div style={{ padding: '20px 24px' }}>
                <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600 }}>
                  单据标注结果
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {/* 预期结果 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#1890FF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        width: '3px',
                        height: '14px',
                        background: '#1890FF',
                        borderRadius: '2px'
                      }}></span>
                      预期结果
                    </div>
                    <div style={{
                      background: '#f8f9fa',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      fontFamily: 'Monaco, Menlo, Consolas, monospace',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre', wordBreak: 'normal' }}>
                        {annotationExpectedAnswer 
                          ? (typeof annotationExpectedAnswer === 'string' 
                              ? annotationExpectedAnswer 
                              : JSON.stringify(annotationExpectedAnswer, null, 2))
                          : '暂无数据'}
                      </pre>
                    </div>
                  </div>

                  {/* 机审结果 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#52C41A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        width: '3px',
                        height: '14px',
                        background: '#52C41A',
                        borderRadius: '2px'
                      }}></span>
                      机审结果
                    </div>
                    <div style={{
                      background: '#f8f9fa',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      fontFamily: 'Monaco, Menlo, Consolas, monospace',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre', wordBreak: 'normal' }}>
                        {annotationActualResult 
                          ? (typeof annotationActualResult === 'string' 
                              ? annotationActualResult 
                              : JSON.stringify(annotationActualResult, null, 2))
                          : '暂无数据'}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default JudgeTaskList;
